import * as THREE from "three";
import { mergeGeometries } from "three/examples/jsm/utils/BufferGeometryUtils.js";
import { entityDefinitions, RAMP_SLOPE, ROOF_HEIGHT, trailerDepth } from "../data/entities";
import type { EntityKind, WorldEntity } from "../data/model";
import { camera as CAM, motor as M, world as W } from "../data/tuning";
import { CAR_HALF_LENGTH, overlapX, overlapZ, surfaceTop, type CarBox } from "../engine/Collision";
import type { RunSnapshot } from "../engine/RunSession";
import type { Assets } from "./Assets";
import { DebugBoxes } from "./DebugBoxes";
import { acesFilmic, DynamicResolution, pickQuality, PostFX, skyEnvironment, type Quality } from "./PostFX";
import { animateProp, buildEntityProp, buildGasCop, buildHoverBoard } from "./props";
import { CAR_YAW, caughtPose, keepClear, pickSide, restPose, type Area, type Pose2 } from "./caught";
import { CoinField } from "./props/coinField";
import { createBillboards, type Billboards } from "./easterEggs";
import { buildIntroSet, RunIntro, type IntroHooks, type IntroSet } from "./intro";
import { PropField, type PropProto } from "./propField";
import { buildRoadster, skinById, type RoadsterParts } from "./Roadster";
import { NightMode, withNightGlow, type TimeOfDay } from "./night";

/** Three-step cel shading (the roadster's toy look). */
function gradientMap(): THREE.DataTexture {
  const data = new Uint8Array([110, 110, 110, 255, 190, 190, 190, 255, 255, 255, 255, 255]);
  const texture = new THREE.DataTexture(data, 3, 1, THREE.RGBAFormat);
  texture.minFilter = THREE.NearestFilter;
  texture.magFilter = THREE.NearestFilter;
  texture.needsUpdate = true;
  return texture;
}

/** The kickflip: one full roll per lane change, in the swipe's direction (the video's move). */
const FLIP_SECONDS = 0.42;
const FLIP_LIFT = 0.9;

const LANE = W.laneWidth;
const ROAD_HALF = LANE * 1.5 + 0.6;
const SEGMENT = 12;
const SEGMENTS = 40;
const LOOP = SEGMENT * SEGMENTS;
const CITY = 9;                        // Kenney city kits: 1 tile = 9 m (toy-town scale next to a 12 m road)
const PURPLE = 0x9945ff;
const GREEN = 0x14f195;
const SKY_TOP = 0x1a64d8;
const SKY_HORIZON = 0x7fb6f5;
/** the haze where the city meets the sky: the fog, the sky's horizon band and the clear colour are all this */
const HAZE = 0x8fc2f7;
/** ACES exposure (see PostFX.ts) */
const EXPOSURE = 1.05;
/**
 * The sun: high, from behind the camera on the right — the faces you drive at are lit and the shadows fall ahead and
 * to the left, where you can read them. The sun you see in the sky (ahead, on the left) is set dressing.
 */
const SUN_DIR = new THREE.Vector3(-0.5, 0.74, -0.45).normalize();
const SKY_SUN = new THREE.Vector3(0.3, 0.36, 0.88).normalize();
const SUN_DISTANCE = 120;
/** the sun's shadow box around the car (metres): 22 behind, 64 ahead, up to a thruster flight */
const SHADOW_BOX = { x: 15, yMin: -1, yMax: 24, zMin: -22, zMax: 64 };
/** the 1024 map's box: the road around the car only (fewer casters, sharper texels) */
const SHADOW_BOX_TIGHT = { x: 11, yMin: -1, yMax: 22, zMin: -12, zMax: 46 };
/** what the car drives on: the only entities that take shadows */
const DRIVABLE: EntityKind[] = ["truck", "mover", "ramp", "deck", "platform"];
/** small solid obstacles that get the high-contrast danger box */
const DANGER_KINDS: EntityKind[] = ["barrierLow", "barrierBar", "barrierHigh", "blocker", "pole", "pillar", "bush", "dumpster", "powerBox", "gateWall", "ethCoin"];
/** things that keep their soft blob and cast no sun shadow: SOL, power-ups, letters, tokens, the flat pads */
const NO_SUN_SHADOW: EntityKind[] = ["coin", "pickup", "letter", "token", "speedPadBoost", "speedPadSlow"];
/** the camera looks down +z, where Three.js puts +x on the screen's left — so world x is mirrored on the way to the scene */
const VX = -1;
const PULLBACK = { y: 0.8, z: -2.2 };  // our car is 4.2 m long; the original runner is 1.8 m tall
const PICKUPS: EntityKind[] = ["pickup", "key", "mysteryBox", "letter", "token"];
/** every kind `buildEntityProp` draws (coins aside: CoinField): drawn instanced through PropField, no object per entity */
const PROP_KINDS = new Set<EntityKind>(["truck", "mover", "ramp", "barrierLow", "barrierBar", "barrierHigh", "pickup", "key", "mysteryBox", "letter", "token",
  "deck", "gateWall", "dumpster", "powerBox", "pole", "bush", "pillar"]);
/** a road prop on screen: its entity (identity: ids restart with every run), its model and its smoothed x */
interface LiveProp { entity: WorldEntity; proto: PropProto; x: number; }
const MID_BUILDINGS = ["city-kit-commercial/building-a", "city-kit-commercial/building-b", "city-kit-commercial/building-c", "city-kit-commercial/building-d",
  "city-kit-commercial/building-e", "city-kit-commercial/building-f", "city-kit-commercial/building-g", "city-kit-commercial/building-h",
  "city-kit-commercial/building-i", "city-kit-commercial/building-j", "city-kit-suburban/building-type-a", "city-kit-suburban/building-type-c",
  "city-kit-suburban/building-type-f", "city-kit-suburban/building-type-h", "city-kit-suburban/building-type-k", "city-kit-suburban/building-type-m",
  "city-kit-suburban/building-type-p", "city-kit-suburban/building-type-s"];
const TALL_BUILDINGS = ["city-kit-commercial/building-skyscraper-a", "city-kit-commercial/building-skyscraper-b", "city-kit-commercial/building-skyscraper-c",
  "city-kit-commercial/building-skyscraper-d", "city-kit-commercial/building-skyscraper-e"];
const FAR_BUILDINGS = ["city-kit-commercial/low-detail-building-a", "city-kit-commercial/low-detail-building-b", "city-kit-commercial/low-detail-building-c",
  "city-kit-commercial/low-detail-building-d", "city-kit-commercial/low-detail-building-e", "city-kit-commercial/low-detail-building-wide-a",
  "city-kit-commercial/low-detail-building-wide-b"];
const INDUSTRY = ["city-kit-industrial/building-a", "city-kit-industrial/building-c", "city-kit-industrial/building-f", "city-kit-industrial/water-tower", "city-kit-industrial/detail-tank"];
const CONTAINERS = ["city-kit-industrial/shipping-container-a", "city-kit-industrial/shipping-container-b", "city-kit-industrial/shipping-container-c"];

interface Particle { sprite: THREE.Sprite; vx: number; vy: number; vz: number; life: number; total: number; }
interface Vehicle { root: THREE.Group; wheels: THREE.Object3D[]; length: number; height: number; }
/** The shop's picture stage: one offscreen renderer, lights and sky for every car and board preview. */
interface PreviewStage { renderer: THREE.WebGLRenderer; scene: THREE.Scene; camera: THREE.PerspectiveCamera; environment: THREE.WebGLRenderTarget; }

function canvasTexture(width: number, height: number, draw: (ctx: CanvasRenderingContext2D) => void): THREE.CanvasTexture {
  const canvas = document.createElement("canvas");
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext("2d");
  if (ctx) draw(ctx);
  const texture = new THREE.CanvasTexture(canvas);
  texture.colorSpace = THREE.SRGBColorSpace;
  texture.anisotropy = 4;
  return texture;
}

function blobTexture(): THREE.CanvasTexture {
  return canvasTexture(128, 128, (ctx) => {
    const g = ctx.createRadialGradient(64, 64, 6, 64, 64, 64);
    g.addColorStop(0, "rgba(30,26,60,0.5)");
    g.addColorStop(1, "rgba(30,26,60,0)");
    ctx.fillStyle = g;
    ctx.fillRect(0, 0, 128, 128);
  });
}

/** A small seeded random, so the painted textures come out the same on every load. */
function seeded(seed: number): () => number {
  let s = seed >>> 0;
  return () => {
    s = (s + 0x6d2b79f5) >>> 0;
    let t = s;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

/**
 * One slice of road (`width` across, `length` along): asphalt with fine grain, a few darker patches and polished
 * wheel tracks, the lane dashes (3 m on, 3 m off) and the yellow edge lines painted in. Seamless along the road.
 */
function roadTexture(width: number, length: number, lane: number): THREE.CanvasTexture {
  const W = 1024, H = 1024;
  const px = W / width, pz = H / length;
  const random = seeded(7);
  return canvasTexture(W, H, (ctx) => {
    ctx.fillStyle = "#545869";
    ctx.fillRect(0, 0, W, H);
    for (let i = 0; i < 26; i++) {
      const x = random() * W, y = random() * H, r = 30 + random() * 110, a = 0.04 + random() * 0.06;
      const dark = random() < 0.6;
      for (const dy of [-H, 0, H]) {                                  // drawn three times so it wraps along the road
        const g = ctx.createRadialGradient(x, y + dy, 0, x, y + dy, r);
        g.addColorStop(0, dark ? `rgba(20,22,34,${a})` : `rgba(150,154,170,${a})`);
        g.addColorStop(1, "rgba(0,0,0,0)");
        ctx.fillStyle = g;
        ctx.fillRect(x - r, y + dy - r, r * 2, r * 2);
      }
    }
    for (const centre of [-lane, 0, lane]) {
      for (const off of [-0.9, 0.9]) {
        const x = (centre + off + width / 2) * px;
        const g = ctx.createLinearGradient(x - 0.5 * px, 0, x + 0.5 * px, 0);
        g.addColorStop(0, "rgba(25,27,40,0)"); g.addColorStop(0.5, "rgba(25,27,40,0.13)"); g.addColorStop(1, "rgba(25,27,40,0)");
        ctx.fillStyle = g;
        ctx.fillRect(x - 0.5 * px, 0, px, H);
      }
    }
    const image = ctx.getImageData(0, 0, W, H);
    const data = image.data;
    for (let i = 0; i < data.length; i += 4) {
      const n = (random() - 0.5) * 20 + (random() < 0.012 ? 30 : 0);
      data[i] += n; data[i + 1] += n; data[i + 2] += n;
    }
    ctx.putImageData(image, 0, 0);
    const paint = (x: number, z0: number, z1: number, w: number, color: string) => {
      ctx.fillStyle = color;
      ctx.fillRect((x + width / 2 - w / 2) * px, z0 * pz, w * px, (z1 - z0) * pz);
    };
    for (const x of [-lane / 2, lane / 2]) { paint(x, 1.5, 4.5, 0.18, "#f3f4f7"); paint(x, 7.5, 10.5, 0.18, "#f3f4f7"); }
    for (const x of [-lane * 1.5 - 0.15, lane * 1.5 + 0.15]) paint(x, 0, length, 0.2, "#f2c94c");
  });
}

/** Sidewalk paving: light concrete slabs of 1.5 m, each a shade apart, with darker joints. Seamless along the road. */
function pavingTexture(width: number, length: number): THREE.CanvasTexture {
  const W = 256, H = 512;
  const random = seeded(11);
  return canvasTexture(W, H, (ctx) => {
    const cols = Math.max(1, Math.round(width / 1.5)), rows = Math.max(1, Math.round(length / 1.5));
    const cw = W / cols, rh = H / rows;
    for (let r = 0; r < rows; r++) for (let c = 0; c < cols; c++) {
      const v = 224 + Math.floor((random() - 0.5) * 14);
      ctx.fillStyle = `rgb(${v - 5},${v - 4},${v + 4})`;
      ctx.fillRect(c * cw, r * rh, cw, rh);
    }
    ctx.strokeStyle = "rgba(118,114,138,0.55)";
    ctx.lineWidth = 2;
    for (let c = 0; c <= cols; c++) { ctx.beginPath(); ctx.moveTo(c * cw, 0); ctx.lineTo(c * cw, H); ctx.stroke(); }
    for (let r = 0; r <= rows; r++) { ctx.beginPath(); ctx.moveTo(0, r * rh); ctx.lineTo(W, r * rh); ctx.stroke(); }
  });
}

/** A ramp: a wedge rising over `slope` metres to `height`, then flat to `length`. Centred on z, bottom at y = 0. */
function rampGeometry(width: number, height: number, length: number, slope: number): THREE.BufferGeometry {
  const w = width / 2;
  const z0 = -length / 2;
  const z1 = z0 + slope;
  const z2 = length / 2;
  const shape: [number, number][] = [[z0, 0], [z1, height], [z2, height], [z2, 0]];   // side profile (z, y)
  const positions: number[] = [];
  // wound so every face points OUT (it was inside out: the wedge was culled from outside and the ramp looked hollow)
  const quad = (a: number[], b: number[], c: number[], d: number[]) => positions.push(...a, ...c, ...b, ...a, ...d, ...c);
  // top surfaces (slope, flat), back, bottom, two sides
  quad([-w, 0, z0], [w, 0, z0], [w, height, z1], [-w, height, z1]);
  quad([-w, height, z1], [w, height, z1], [w, height, z2], [-w, height, z2]);
  quad([w, 0, z2], [-w, 0, z2], [-w, height, z2], [w, height, z2]);
  quad([-w, 0, z2], [w, 0, z2], [w, 0, z0], [-w, 0, z0]);
  for (const side of [-1, 1]) {
    const x = side * w;
    const p = shape.map(([z, y]) => [x, y, z]);
    if (side > 0) { positions.push(...p[0], ...p[1], ...p[3], ...p[1], ...p[2], ...p[3]); }
    else { positions.push(...p[0], ...p[3], ...p[1], ...p[1], ...p[3], ...p[2]); }
  }
  const geometry = new THREE.BufferGeometry();
  geometry.setAttribute("position", new THREE.Float32BufferAttribute(positions, 3));
  geometry.computeVertexNormals();
  return geometry;
}

/** A slice's handle on what it put into the city batches (freed when the slice re-dresses). */
type CityHandle = { batch: THREE.BatchedMesh; id: number } | { mesh: THREE.Mesh };

/**
 * The city as a handful of draw calls: every slice's buildings, lamps, trees and boxes go into one BatchedMesh per
 * material (Kenney paints a whole kit from one colour map; plain boxes share one material and get their colour per
 * instance), placed in "city space" — a group that slides back as you drive, so no instance moves per frame. A
 * slice that wraps frees its instances and re-dresses. Per-instance culling against the camera and the sun keeps the
 * vertex work to what is on screen and to the lamps near the car. Anything unexpected stays a plain mesh.
 */
class CityBatches {
  readonly group = new THREE.Group();
  private batches = new Map<THREE.Material, { batch: THREE.BatchedMesh; ids: Map<THREE.BufferGeometry, number> }>();
  private flat?: { batch: THREE.BatchedMesh; ids: Map<THREE.BufferGeometry, number> };
  private matrix = new THREE.Matrix4();
  private color = new THREE.Color();

  /** `parts`: every (geometry, material) a slice can use; `boxes`: the plain box geometries; `casters`: materials that cast the sun's shadow. */
  /** the kit materials of the batches (night.ts patches them before their first compile) */
  readonly materials: THREE.Material[] = [];

  constructor(parts: { geometry: THREE.BufferGeometry; material: THREE.Material }[], boxes: THREE.BufferGeometry[], casters: Set<THREE.Material>,
    prepare: (geometry: THREE.BufferGeometry, material: THREE.Material) => THREE.BufferGeometry = (g) => g) {
    const byMaterial = new Map<THREE.Material, Set<THREE.BufferGeometry>>();
    for (const { geometry, material } of parts) {
      if (!byMaterial.has(material)) byMaterial.set(material, new Set());
      byMaterial.get(material)?.add(geometry);
    }
    const make = (material: THREE.Material, geometries: THREE.BufferGeometry[]) => {
      let vertices = 0, indices = 0;
      for (const g of geometries) { vertices += g.attributes.position.count; indices += g.index?.count ?? 0; }
      const batch = new THREE.BatchedMesh(256, vertices, indices, material);
      batch.frustumCulled = false;                                 // the whole city is always around; instances are culled one by one
      batch.sortObjects = false;
      const ids = new Map<THREE.BufferGeometry, number>();
      for (const g of geometries) ids.set(g, batch.addGeometry(g));
      this.group.add(batch);
      return { batch, ids };
    };
    for (const [material, set] of byMaterial) {
      // each kit geometry as the batch holds it (night: with its window seeds), still found by the original
      const prepared = [...set].map((g) => prepare(g, material));
      const entry = make(material, prepared);
      entry.ids = new Map([...set].map((g, i) => [g, entry.ids.get(prepared[i]) as number]));
      this.materials.push(material);
      entry.batch.castShadow = casters.has(material);
      this.batches.set(material, entry);
    }
    if (boxes.length) {
      this.flat = make(new THREE.MeshLambertMaterial({ color: 0xffffff }), boxes);
    }
  }

  /** Moves a dressed slice (children placed around its centre) into the batches at city-space z = `cz`. */
  take(slice: THREE.Group, cz: number): CityHandle[] {
    const handles: CityHandle[] = [];
    slice.position.set(0, 0, 0);
    slice.updateMatrixWorld(true);
    const meshes: THREE.Mesh[] = [];
    slice.traverse((o) => { if ((o as THREE.Mesh).isMesh) meshes.push(o as THREE.Mesh); });
    for (const mesh of meshes) {
      this.matrix.copy(mesh.matrixWorld);
      this.matrix.elements[14] += cz;
      const material = mesh.material as THREE.Material;
      const kit = Array.isArray(mesh.material) ? undefined : this.batches.get(material);
      const plain = !kit && !Array.isArray(mesh.material) && !(material as THREE.MeshStandardMaterial).map ? this.flat : undefined;
      const entry = kit ?? plain;
      const geometryId = entry?.ids.get(mesh.geometry);
      if (entry && geometryId !== undefined) {
        const id = entry.batch.addInstance(geometryId);
        entry.batch.setMatrixAt(id, this.matrix);
        if (entry === this.flat) entry.batch.setColorAt(id, this.color.copy((material as THREE.MeshStandardMaterial).color));
        handles.push({ batch: entry.batch, id });
      } else {
        mesh.removeFromParent();                                   // not batchable: kept as a plain mesh in city space
        mesh.matrixAutoUpdate = false;
        mesh.matrix.copy(this.matrix);
        this.group.add(mesh);
        handles.push({ mesh });
      }
    }
    slice.clear();
    return handles;
  }

  release(handles: CityHandle[] | undefined): void {
    for (const handle of handles ?? []) {
      if ("batch" in handle) handle.batch.deleteInstance(handle.id);
      else handle.mesh.removeFromParent();
    }
  }

  /** Shifts every instance along z (keeps city-space numbers small on long runs). */
  shift(handles: CityHandle[], dz: number): void {
    for (const handle of handles) {
      if ("batch" in handle) {
        handle.batch.getMatrixAt(handle.id, this.matrix);
        this.matrix.elements[14] += dz;
        handle.batch.setMatrixAt(handle.id, this.matrix);
      } else {
        handle.mesh.matrix.elements[14] += dz;
        handle.mesh.matrixWorldNeedsUpdate = true;
      }
    }
  }
}

/**
 * The day city on Kenney's CC0 kits: a three-lane highway between pastel buildings and trees, overpasses, a hover
 * car, the GAS COP, road trains of ETH containers, barriers, traffic lights, hedges, SOL. Entities come from the
 * engine; the city recycles and re-dresses itself as you drive.
 */
export class GameScene {
  readonly renderer: THREE.WebGLRenderer;
  private scene = new THREE.Scene();
  private camera = new THREE.PerspectiveCamera(60, 1, 0.5, 700);
  private entityObjects = new Map<number, THREE.Object3D>();
  private pools = new Map<string, THREE.Object3D[]>();
  private car = new THREE.Group();
  private roadster?: RoadsterParts;
  private skinId = "";
  private solMark?: THREE.Texture;
  private toon = gradientMap();
  private flipAngle = 0;
  private flipTarget = 0;
  private lastLane = 0;
  private smokeTimer = 0;
  private speedLines: THREE.Mesh[] = [];
  private speedLineBatch?: THREE.InstancedMesh;
  /** one of every prop, tiny and behind the camera for the first frames: their shaders (and shadow shaders) compile at load */
  /** SOL and ETH coins: two instanced fields (≈ 10 draws for every coin on the road) instead of a prop per coin */
  private solCoins = new CoinField("sol");
  private ethCoins = new CoinField("eth");
  private coinIds = new Set<number>();
  /** every other road prop: one InstancedMesh per distinct part, matrices rebuilt each frame (propField.ts) */
  private props!: PropField;
  /** the Easter-egg billboards on the sidewalks (easterEggs.ts: Breakpoint, Solana lore, Excel jokes; one draw each) */
  private eggs?: Billboards;
  /** dev ?holdsigns: the billboards stay put (promo stills) */
  private holdSigns = false;
  private live = new Map<number, LiveProp>();
  /** a phone or tablet (coarse pointer): desktop screens render at pixel ratio 1, the HUD stays crisp anyway */
  private touch = typeof matchMedia === "function" && matchMedia("(pointer: coarse)").matches;
  private statsDeep = new URLSearchParams(location.search).get("stats") === "deep";
  /** the surface right under the car (road, roof, ramp slope, deck) and the lowest bridge deck above it (QA-15, QA-22) */
  private ground = 0;
  private ceiling = Infinity;
  private probe: CarBox = { x: 0, z: 0, y: 0, height: 1.3, halfWidth: 0.95, halfLength: CAR_HALF_LENGTH, vy: 0 };
  /** high-contrast obstacles: a red-orange box over every small solid obstacle's collision box (shared: toggles at once) */
  private dangerMaterial = new THREE.MeshBasicMaterial({ color: 0xff5a1f, transparent: true, opacity: 0.38, depthWrite: false, visible: false });
  private deathTime = -1;
  /** the caught sequence (caught.ts): the pickup's side (+1 = +x; the car's spin turns that way), its pose at the
   *  catch and where it stops, the camera's swing (from the other side), and the side a tie takes next time */
  private deathSide: 1 | -1 = 1;
  private deathFrom: Pose2 = { x: 0, z: 0, yaw: 0 };
  private deathFromY = 0;
  private deathRest: Pose2 = { x: 0, z: 0, yaw: 0 };
  private deathSwing = 0.9;
  private deathAlternate: 1 | -1 = 1;
  private readonly deathRadius = 8.5;
  /** the caught camera looks this share of the way from the car to the pickup's stop: both in the picture */
  private readonly deathLook = 0.35;
  /** dev `?catch=<ms>`: the caught sequence starts that long into a run (add `&freeze=<ms>` so the road stops too);
   *  `?catchHold=<ms>` jumps to that moment after the catch and holds it (screenshots) */
  private devCatch = import.meta.env.DEV ? (Number(new URLSearchParams(location.search).get("catch")) || (new URLSearchParams(location.search).has("catch") ? 1000 : 0)) : 0;
  private devCatchHold = import.meta.env.DEV ? Number(new URLSearchParams(location.search).get("catchHold") ?? 0) / 1000 : 0;
  private devRunTime = 0;
  private chaser = new THREE.Group();
  private chaserVehicle?: Vehicle;
  private hoverGlow?: THREE.Mesh;
  private shieldGlow?: THREE.Mesh;
  private board?: THREE.Group;
  private boardId = "default";
  private preview?: PreviewStage;
  /** `?boxes`: every collision box drawn over the models (DebugBoxes.ts) */
  private boxes?: DebugBoxes;
  private lastBoard = 0;
  private carShadow?: THREE.Mesh;
  private segments: THREE.Group[] = [];
  private city?: CityBatches;
  private cityScroll = 0;
  private segmentCounter = 0;
  private solFace: THREE.MeshBasicMaterial;
  private ethFace: THREE.MeshBasicMaterial;
  private ethDecal: THREE.MeshStandardMaterial;
  private blob = blobTexture();
  private textures = new Map<string, THREE.Texture>();
  private materials = new Map<string, THREE.Material>();
  private particles: Particle[] = [];
  private particlePool: THREE.Sprite[] = [];
  private clock = 0;
  private running = false;
  private settings = { reducedMotion: false, highContrast: false, screenShake: true };
  private shake = 0;
  private carZ = 0;
  /** the run's speed this frame (things blown off the road drift back with it) */
  private roadSpeed = 0;
  /** entities already blown away (a WeakSet: entity objects are new every run) */
  private blown = new WeakSet<WorldEntity>();
  private wheelSpin = 0;
  private lastCoins = 0;
  private lastHunted = false;
  private wasAirborne = false;
  private clouds = new THREE.Group();
  private random = Math.random;
  // the look: lights, shadows, environment, post (PostFX.ts)
  /** high (post + sun shadows) or low (weak phones); pinned by ?quality; the render scale adapts on its own */
  private quality: Quality = "high";
  private qualityForced = false;
  private resolution = new DynamicResolution();
  private post?: PostFX;
  private cheapCar = false;
  private carSwap = new Map<THREE.Material, THREE.Material>();
  private skyline?: THREE.Object3D;
  private activeIds = new Set<number>();
  private deathTarget = new THREE.Vector3();
  private deathEye = new THREE.Vector3();
  private devLogAt = 0;
  private sun = new THREE.DirectionalLight(0xfff0dc, 3.3);
  private hemi = new THREE.HemisphereLight(0xcfe2ff, 0xb3a89c, 1.1);   // matte surfaces get their whole ambient here
  private environment?: THREE.WebGLRenderTarget;
  private haze = new THREE.Color(HAZE);
  /** the soft blob under things that cast a real shadow on high: shown on low only */
  private blobFallback = new THREE.MeshBasicMaterial({ map: this.blob, transparent: true, depthWrite: false });
  /** every blob-shadow material that only shows on low (the one above, and copies of the props' blobs) */
  private fallbacks: THREE.Material[] = [this.blobFallback];
  private shadowAxes = { right: new THREE.Vector3(), up: new THREE.Vector3(), texelX: 0.05, texelY: 0.05 };
  private shadowFocus = new THREE.Vector3();
  private shadowSize = 0;
  private shadowCentre = 21;
  private stats = new URLSearchParams(location.search).has("stats");
  private statsAt = 0;
  /** the street's painted textures slide with the city (one long road mesh instead of one per slice) */
  private streetMaps: THREE.Texture[] = [];
  private streetScroll = 0;
  /** the opening scene between the tap and the run (intro.ts): its set is built once, the scene per run */
  private introSet!: IntroSet;
  private intro?: RunIntro;
  /** night (night.ts): the sky, lights and fog after dark, the city's lit windows and lamps; day = as it always was */
  private night!: NightMode;
  private skyMaterial!: THREE.ShaderMaterial;
  /** billboard roots lit at night (the game's own; a dev page adds its own through watchBillboards) */
  private nightSigns: THREE.Object3D[] = [];
  private lampX = ROAD_HALF + 3.0 + 7.5 * 0.0875 / 0.675;

  constructor(private host: HTMLElement, private assets: Assets) {
    // the canvas's MSAA serves low; on high the scene goes through HDR targets and SMAA does the edges (PostFX.ts)
    const picked = pickQuality();
    this.quality = picked.quality;
    this.qualityForced = picked.forced;
    // canvas MSAA only for low: on high the scene is drawn into HDR targets and SMAA does the edges
    this.renderer = new THREE.WebGLRenderer({ antialias: picked.quality === "low", alpha: false, stencil: false, powerPreference: "high-performance" });
    // three.js checks every new shader program for errors with a blocking driver call (getProgramInfoLog): 28 % of the CPU
    // on the owner's laptop. Off outside ?debug, as three.js recommends for production.
    this.renderer.debug.checkShaderErrors = import.meta.env.DEV && new URLSearchParams(location.search).has("debug");
    this.renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, 2));
    this.renderer.outputColorSpace = THREE.SRGBColorSpace;
    this.renderer.toneMapping = THREE.ACESFilmicToneMapping;
    this.renderer.toneMappingExposure = EXPOSURE;
    this.renderer.shadowMap.type = THREE.PCFShadowMap;            // 4× cheaper per receiving pixel than PCFSoft
    this.renderer.info.autoReset = false;                       // counted per frame: scene + shadow map + post
    const halfFloat = this.renderer.extensions.has("EXT_color_buffer_float") || this.renderer.extensions.has("EXT_color_buffer_half_float");
    if (!halfFloat) this.quality = "low";                          // no HDR targets: no post (and no MSAA: rare)
    this.renderer.domElement.className = "game-canvas";
    this.host.append(this.renderer.domElement);
    this.camera.position.set(0, CAM.offset.y + PULLBACK.y, CAM.offset.z + PULLBACK.z);
    this.camera.lookAt(0, CAM.offset.y + CAM.focus.y, CAM.focus.z + CAM.offset.z);
    const loader = new THREE.TextureLoader();
    const solMark = loader.load(`${import.meta.env.BASE_URL}assets/sol-mark.png`, () => { this.refreshCoinFace(solMark); this.rebuildRoadster(); });
    this.solMark = solMark;
    const eth = loader.load(`${import.meta.env.BASE_URL}assets/eth-glyph.png`, () => this.refreshEthFace(eth));
    solMark.colorSpace = THREE.SRGBColorSpace;
    eth.colorSpace = THREE.SRGBColorSpace;
    this.solFace = new THREE.MeshBasicMaterial({ color: 0xffffff });
    this.ethFace = new THREE.MeshBasicMaterial({ color: 0x111114 });
    this.ethDecal = new THREE.MeshStandardMaterial({ map: eth, transparent: true, alphaTest: 0.3, side: THREE.DoubleSide, roughness: 0.55, metalness: 0, emissive: 0xffffff, emissiveMap: eth, emissiveIntensity: 0.12 });
    this.buildWorld();
    this.buildSpeedLines();
    this.scene.add(this.solCoins.root, this.ethCoins.root);
    this.props = new PropField(this.geo("unit-box", () => new THREE.BoxGeometry(1, 1, 1)), this.dangerMaterial);
    this.scene.add(this.props.root);
    this.setSkin("pearl");
    this.buildChaser();
    this.applyQuality(this.quality);                             // also sizes everything
    if (new URLSearchParams(location.search).has("boxes")) this.boxes = new DebugBoxes(this.scene);
    this.introSet = buildIntroSet(this.glowTexture());              // hidden until a run's opening (warmUp compiles it)
    this.scene.add(this.introSet.root);
    // dev ?signs=vibhu,gm&signz=18,40: those signs at those distances (promo stills); ?holdsigns keeps them there
    const sq = new URLSearchParams(location.search);
    const fixedSigns = import.meta.env.DEV && sq.get("signs") ? { ids: (sq.get("signs") as string).split(","), z: (sq.get("signz") ?? "18").split(",").map(Number) } : undefined;
    this.holdSigns = import.meta.env.DEV && sq.has("holdsigns");
    this.eggs = createBillboards(this.scene, { renderer: this.renderer, spot: this.signSpot, fixed: fixedSigns });   // before warmUp: its material compiles at load
    this.setupNight();                                              // before warmUp: its materials and fx compile at load too
    this.warmUp();
    window.addEventListener("resize", this.resize);
  }

  setSettings(reducedMotion: boolean, highContrast: boolean, screenShake: boolean): void {
    this.settings = { reducedMotion, highContrast, screenShake };
    this.dangerMaterial.visible = highContrast;                    // every obstacle, pooled or new, at once (QA-16)
  }

  setRunning(running: boolean): void {
    this.running = running;
    this.endIntro();                                               // a skip (the run starts) or any other screen cuts it short
  }

  /**
   * The opening scene between the tap and the run (intro.ts): the car on its charging pad, the owner's pickup, the swing
   * into the chase camera. `hooks.done` fires when it ends on its own (start the run then); the run starting earlier (a
   * skip) cuts it to its last frame. `freezeMs` (?freeze, screenshots) holds it at that moment.
   */
  playIntro(hooks: IntroHooks, freezeMs = 0): void {
    this.endIntro();
    const roadster = this.roadster;
    if (!roadster) { queueMicrotask(() => hooks.done(0)); return; }
    this.intro = new RunIntro({
      camera: this.camera, host: this.host, car: this.car, chaser: this.chaser,
      carLength: roadster.length, chaserLength: this.chaserVehicle?.length ?? 4, chaserHeight: this.chaserVehicle?.height ?? 2,
      hoverGlow: this.hoverGlow, carShadow: this.carShadow,
      chase: { height: CAM.offset.y + PULLBACK.y, back: -(CAM.offset.z + PULLBACK.z), lookDown: CAM.focus.y - 0.6, lookAhead: CAM.focus.z + CAM.offset.z },
      baseFov: () => (this.camera.aspect < 0.8 ? CAM.fov : 58),
      body: () => this.roadster?.body,
      jets: (down, back) => this.setJets(down, back),
      wheels: (folded) => this.setRoadsterWheels(folded),
      chaserWheels: () => this.setWheels(this.chaserVehicle, false),
      spin: (metres) => { this.wheelSpin += metres / 0.38; },
      burst: (x, y, z, color, count, speed, soft) => this.burst(x, y, z, color, count, speed, soft),
      lights: (fast) => this.flashChaser(fast)
    }, this.introSet, hooks, freezeMs);
  }

  /** Cuts the opening scene to its last frame (= the run's first) and strikes its set. */
  endIntro(): void {
    this.intro?.finish();
    this.intro = undefined;
  }

  /** The garage's choice: a roadster paint job. */
  setSkin(id: string): void {
    if (id === this.skinId) return;
    this.skinId = id;
    const skin = skinById(id);
    if (this.roadster) {
      const cheap = this.cheapCar;
      this.setCheapCar(false);                                       // the paint job lands on the real materials…
      this.roadster.setSkin(skin);
      this.carSwap.clear();
      this.setCheapCar(cheap);                                       // …and the cheap copies are made fresh from them
      return;
    }
    const roadster = buildRoadster(this.toon, skin, this.solMark?.image ? this.solMark : undefined, { detail: "game" });   // ≤ 15k triangles, a shadow proxy
    this.roadster = roadster;
    this.car.add(roadster.root);
    this.hoverGlow = new THREE.Mesh(new THREE.PlaneGeometry(2.4, roadster.length * 0.95), new THREE.MeshBasicMaterial({ map: this.glowTexture(), color: 0x7fe9ff, transparent: true, opacity: 0.28, depthWrite: false, blending: THREE.AdditiveBlending }));
    this.hoverGlow.rotation.x = -Math.PI / 2;
    this.hoverGlow.position.y = 0.05;
    this.car.add(this.hoverGlow);
    this.shieldGlow = new THREE.Mesh(new THREE.SphereGeometry(1, 20, 14), new THREE.MeshBasicMaterial({ color: GREEN, transparent: true, opacity: 0.18, depthWrite: false }));
    this.shieldGlow.scale.set(1.4, 1.0, roadster.length * 0.6);
    this.shieldGlow.position.y = 0.75;
    this.shieldGlow.visible = false;
    roadster.body.add(this.shieldGlow);
    // the hover board under the car while one is active: the garage's design (props/boards.ts, see setBoard)
    this.board = buildHoverBoard(this.boardId);
    this.board.visible = false;
    roadster.body.add(this.board);
    this.carShadow = new THREE.Mesh(new THREE.PlaneGeometry(3.0, roadster.length + 1.6), this.blobFallback);
    this.carShadow.rotation.x = -Math.PI / 2;
    this.carShadow.position.y = 0.02;
    this.car.add(this.carShadow);
    if (!this.car.parent) this.scene.add(this.car);
    this.prepareCar();
  }

  /** Once the Solana mark has loaded, the car is rebuilt with it on the hood and doors. */
  private rebuildRoadster(): void {
    if (!this.roadster) return;
    this.car.remove(this.roadster.root);
    const skin = skinById(this.skinId || "pearl");
    const fresh = buildRoadster(this.toon, skin, this.solMark, { detail: "game" });
    if (this.shieldGlow) fresh.body.add(this.shieldGlow);
    if (this.board) fresh.body.add(this.board);
    this.roadster = fresh;
    this.car.add(fresh.root);
    this.prepareCar();
    this.carSwap.clear();
    this.setCheapCar(this.cheapCar);
  }

  /** A roadster with the given paint for the shop: rendered once to an image (on the shared preview stage). */
  skinPreview(id: string): string {
    const stage = this.previewStage();
    const car = buildRoadster(this.toon, skinById(id), this.solMark?.image ? this.solMark : undefined);
    car.root.rotation.y = -0.65;
    stage.camera.position.set(5.8, 2.7, 6.2);
    stage.camera.lookAt(0, 0.55, 0);
    return this.snapshotOf(car.root);
  }

  /** The garage's hover board: ridden under the car while a board is active. */
  setBoard(id: string): void {
    if (id === this.boardId) return;
    this.boardId = id;
    if (!this.board) return;                                       // the car (and its board) is built in setSkin
    const parent = this.board.parent;
    const visible = this.board.visible;
    this.board.removeFromParent();                                 // shares geometry and materials: never disposed
    this.board = buildHoverBoard(id);
    this.board.visible = visible;
    parent?.add(this.board);
    this.prepareCar();
  }

  /** A hover board for the shop's BOARDS tab: rendered once to an image (on the shared preview stage). */
  boardPreview(id: string): string {
    const stage = this.previewStage();
    const board = buildHoverBoard(id);
    board.rotation.y = -0.65;
    stage.camera.position.set(4.4, 3.3, 5.0);
    stage.camera.lookAt(0, -0.25, 0);
    return this.snapshotOf(board);
  }

  /** Renders one object alone on the preview stage and hands back a PNG data URL. */
  private snapshotOf(object: THREE.Object3D): string {
    const stage = this.previewStage();
    stage.scene.add(object);
    stage.renderer.render(stage.scene, stage.camera);
    const url = stage.renderer.domElement.toDataURL("image/png");
    stage.scene.remove(object);                                    // shared geometry/materials: nothing to dispose
    return url;
  }

  /** One offscreen renderer for every shop picture, made on first use and kept (a WebGL context per picture stalls phones). */
  private previewStage(): PreviewStage {
    if (this.preview) return this.preview;
    const renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true, preserveDrawingBuffer: true });
    renderer.setPixelRatio(1);
    renderer.setSize(320, 180, false);
    renderer.outputColorSpace = THREE.SRGBColorSpace;
    renderer.toneMapping = THREE.ACESFilmicToneMapping;           // the game's tone map: the garage shows paint as it drives
    renderer.toneMappingExposure = EXPOSURE;
    const scene = new THREE.Scene();
    scene.add(new THREE.HemisphereLight(0xf2f4ff, 0x8f86ad, 1.2));
    const sun = new THREE.DirectionalLight(0xffffff, 1.8);
    sun.position.set(-3, 6, 4);
    scene.add(sun);
    const environment = skyEnvironment(renderer, sun.position.clone().normalize(), {
      top: new THREE.Color(SKY_TOP), horizon: new THREE.Color(HAZE), ground: new THREE.Color(0xa49caa), sun: new THREE.Color(0xfff0dc).multiplyScalar(3)
    });
    scene.environment = environment.texture;
    scene.environmentIntensity = 0.6;
    const camera = new THREE.PerspectiveCamera(32, 320 / 180, 0.1, 50);
    this.preview = { renderer, scene, camera, environment };
    return this.preview;
  }

  triggerShake(): void {
    if (this.settings.screenShake && !this.settings.reducedMotion) this.shake = 0.5;
  }

  render(snapshot: RunSnapshot | undefined, entities: WorldEntity[], dt: number): void {
    this.clock += dt;
    if (snapshot) {
      this.carZ = snapshot.playerZ;
      this.roadSpeed = snapshot.state === "dead" ? 0 : snapshot.speed;
      if (import.meta.env.DEV && this.devCatch) snapshot = this.devCaught(snapshot, dt);
      const dead = snapshot.state === "dead";
      if (dead && this.deathTime < 0) this.startDeath(entities);
      if (!dead && this.deathTime >= 0) this.endDeath();
      this.syncEntities(entities, dt);
      this.measureSurroundings(snapshot, entities);
      this.boxes?.update(entities, this.carZ, snapshot, VX);
      if (dead) this.updateDeath(snapshot, dt);
      else if (this.intro) {
        // the opening scene drives the car, the chaser and the camera; it ends where the run's first frame starts
        const gap = snapshot.chaserDistance + (this.roadster?.length ?? 4.4) / 2 + (this.chaserVehicle?.length ?? 4.0) / 2;
        this.intro.update(dt, { gap, chaserX: VX * snapshot.chaserX, chaserY: snapshot.chaserY });
        if (this.intro.finished) this.intro = undefined;
      } else {
        this.updateCar(snapshot, dt);
        this.updateChaser(snapshot, dt);
        if (this.running) this.scroll(snapshot.speed * dt);
        this.updateCamera(snapshot, dt);
        this.effects(snapshot, dt);
      }
      this.updateSpeedLines(snapshot, dt);
    } else {
      this.idle(dt);
    }
    this.updateParticles(this.intro?.frozen ? 0 : dt);
    this.clouds.position.x = Math.sin(this.clock * 0.02) * 6;
    this.eggs?.animate(dt);
    this.night.update({
      dt, clock: this.clock, quality: this.quality, segments: this.segments, cityScroll: this.cityScroll, lampX: this.lampX,
      entities: snapshot ? entities : [], carZ: this.carZ, vx: VX, car: this.car, carBody: this.roadster?.body, carLength: this.roadster?.length ?? 4.4,
      ground: snapshot ? this.ground : 0, boosters: this.roadster?.boosters ?? [], jets: this.roadster?.jets ?? [], chaser: this.chaser,
      billboards: this.nightSigns, running: this.running
    });
    this.draw(dt);
  }

  destroy(): void {
    window.removeEventListener("resize", this.resize);
    this.post?.dispose();
    this.solCoins.dispose();
    this.ethCoins.dispose();
    this.props.dispose();
    this.eggs?.dispose();
    this.environment?.dispose();
    this.boxes?.dispose();
    if (this.preview) {
      this.preview.environment.dispose();
      this.preview.renderer.dispose();
      this.preview.renderer.forceContextLoss();
    }
    this.renderer.dispose();
    this.renderer.domElement.remove();
  }

  // ------------------------------------------------------------------------------------------------ the look

  /**
   * Before the first run: build one of every road object in every variant the pools key on (truck lengths × paint,
   * parked and oncoming, every power-up, every obstacle), put sample coins in both coin fields, wake everything hidden
   * (board, shield, glows, particles), then compile all shaders for the target the game really draws into (the HDR
   * buffer on high, the canvas on low) and draw one offscreen frame so the shadow pass compiles too. Nothing is shown.
   * Measured on the owner's iGPU: shaders compiled mid-run (each a 50–300 ms stall) were the choppiness.
   */
  private warmUp(): void {
    const made: THREE.Object3D[] = [];
    const instanced: [PropProto, WorldEntity][] = [];
    const group = new THREE.Group();
    group.position.set(0, 0.5, 10);
    let id = 0;
    const sample = (kind: EntityKind, extra: Partial<WorldEntity> = {}) => {
      const def = entityDefinitions[kind];
      const entity = { ...def, id: id++, lane: 0, x: 0, z: 0, speedZ: 0, trailers: 1, active: true, counted: false, ...extra } as WorldEntity;
      if (PROP_KINDS.has(kind)) { instanced.push([this.propFor(entity), entity]); return; }
      const object = this.acquire(entity);
      object.position.set(((made.length % 8) - 4) * 0.6, 0, Math.floor(made.length / 8) * 0.6);
      object.scale.setScalar(0.05);
      object.traverse((o) => { o.frustumCulled = false; });
      group.add(object);
      made.push(object);
    };
    for (const kind of ["truck", "mover"] as EntityKind[]) {
      for (let trailers = 1; trailers <= 5; trailers++) {
        for (let paint = 0; paint < 3; paint++) sample(kind, { trailers, id: paint, depth: trailerDepth(trailers) });
      }
    }
    for (const power of ["magnet", "x2", "sneakers", "jetpack", "pogo", "mystery", "board", "shield", "headstart", "booster"] as const) sample("pickup", { power });
    for (const kind of ["ramp", "barrierLow", "barrierBar", "barrierHigh", "blocker", "pole", "bush", "dumpster", "powerBox", "pillar", "gate", "deck",
      "gateWall", "platform", "speedPadBoost", "speedPadSlow", "key", "mysteryBox", "token"] as EntityKind[]) sample(kind);
    sample("letter", { letter: "S" });
    this.scene.add(group);
    // the instanced props: one of each in front of the camera (drawn only into the offscreen probe below)
    this.props.begin(0);
    instanced.forEach(([proto, entity], i) => this.props.place(proto, -100 - i, ((i % 8) - 4) * 4, 0, 10 + Math.floor(i / 8) * 4, entity));
    this.props.end();
    this.props.setCulling(false);
    this.solCoins.add(-1, 0, 1, 10); this.ethCoins.add(-2, 1, 0, 10);
    this.solCoins.update(0); this.ethCoins.update(0);
    this.burst(0, 1, 10, 0xffffff, 1, 0.1); this.burst(0, 1, 10, 0xffffff, 1, 0.1, true);
    const woken: THREE.Object3D[] = [];
    this.scene.traverse((o) => { if (!o.visible) { o.visible = true; woken.push(o); } });
    const target = this.post ? this.post.sceneTarget : null;
    this.renderer.setRenderTarget(target);
    this.renderer.compile(this.scene, this.camera);
    if (this.renderer.shadowMap.enabled) {
      // the shadow pass (depth programs) only compiles by drawing: one small offscreen frame of the same kind
      const probe = new THREE.WebGLRenderTarget(64, 64, { type: this.post ? THREE.HalfFloatType : THREE.UnsignedByteType });
      this.renderer.setRenderTarget(probe);
      this.renderer.render(this.scene, this.camera);
      probe.dispose();
    }
    this.renderer.setRenderTarget(null);
    for (const o of woken) o.visible = false;
    this.props.setCulling(true);
    this.props.begin(0);
    this.props.end();
    this.solCoins.remove(-1); this.ethCoins.remove(-2);
    this.solCoins.update(0); this.ethCoins.update(0);              // redraw the fields empty (the samples showed on the menu)
    this.scene.remove(group);
    for (const object of made) {
      object.scale.setScalar(1);
      object.position.set(0, 0, 0);
      object.traverse((o) => { o.frustumCulled = true; });
      group.remove(object);
      const key = object.userData.key as string;
      const pool = this.pools.get(key) ?? [];
      pool.push(object);
      this.pools.set(key, pool);
    }
  }

  /**
   * The surface under the car (where its blob shadow lies: road, a roof, a ramp's slope, a deck you are on) and the
   * underside of a bridge deck the car is about to pass under (the camera stays below it). No allocations.
   */
  private measureSurroundings(snapshot: RunSnapshot, entities: WorldEntity[]): void {
    const car = this.probe;
    car.x = snapshot.playerX; car.z = snapshot.playerZ; car.y = snapshot.playerY;
    let ground = 0, ceiling = Infinity;
    for (const e of entities) {
      if (!e.active) continue;
      const k = e.kind;
      if (k === "truck" || k === "mover" || k === "ramp" || k === "platform" || k === "deck") {
        if (overlapX(car, e) >= 0.02 && overlapZ(car, e)) {
          const top = surfaceTop(e, car.z);
          if (top <= car.y + 0.05 && top > ground) ground = top;
        }
      }
      // a deck overhead from 6 m before the car reaches it until the camera (≈ 9 m behind) has come out from under it
      if (k === "deck" && car.y + 1.4 <= e.yBase + 0.5 && car.z > e.z - 6 && car.z < e.z + e.depth + 9) ceiling = Math.min(ceiling, e.yBase - 0.3);
    }
    this.ground = ground;
    this.ceiling = ceiling;
  }

  /** One frame: the shadow box follows the car, the scene goes through post on high, and too slow a high drops to low. */
  private draw(dt: number): void {
    this.renderer.info.reset();
    if (this.quality === "high") this.followSun();
    if (this.post) { this.post.setBloomStrength(this.night.bloom); this.post.render(dt); }
    else this.renderer.render(this.scene, this.camera);
    const now = performance.now();
    if (!this.qualityForced && this.touch && this.resolution.tick(now)) this.resize();
    if (this.stats) this.logStats();
    if (import.meta.env.DEV && now - this.devLogAt > 5000) {
      this.devLogAt = now;
      console.info(`[solrun] quality ${this.quality}${this.qualityForced ? " (forced)" : ""} · ${this.resolution.fps.toFixed(0)} fps · render scale ${this.resolution.scale} · pixel ratio ${this.renderer.getPixelRatio().toFixed(2)} · ${this.renderer.info.render.calls} draws`);
    }
  }

  /** high: the sun's shadow map (1024, a tight box) and post (bloom, grade, SMAA); low: neither — blob shadows, canvas MSAA. */
  private applyQuality(quality: Quality): void {
    this.quality = quality;
    const high = quality === "high";
    this.renderer.shadowMap.enabled = high;
    this.sun.castShadow = high;
    if (high) this.setupShadows(1024);
    for (const material of this.fallbacks) material.visible = !high;
    this.ethCoins.setBlobVisible(!high);
    if (high && !this.post) this.post = new PostFX(this.renderer, this.scene, this.camera);
    if (!high && this.post) { this.post.dispose(); this.post = undefined; }
    this.syncHaze();
    this.setCheapCar(!high);                                       // weak phones: clear-coat paint as plain PBR
    if (this.night) this.night.set(this.night.mode, quality);      // night's extras by tier (no cones, smaller halos on low)
    this.resize();
  }

  /** The fog and the clear colour from the haze (day or night): drawn straight to the screen (low), fog and the clear colour
   *  are mixed in after the tone map, so they match the tone-mapped sky. */
  private syncHaze(): void {
    const fog = this.scene.fog as THREE.Fog;
    if (this.quality === "high") fog.color.copy(this.haze);
    else acesFilmic(this.haze, EXPOSURE, fog.color);
    if (this.scene.background instanceof THREE.Color) this.scene.background.copy(fog.color);
  }

  /** Day or night (Settings → Time of day, the lobby's sun/moon button, ?night / ?day), applied at once — no reload, no compiles. */
  setTimeOfDay(mode: TimeOfDay): void {
    if (mode === this.night.mode) return;
    this.night.set(mode, this.quality);
    this.syncHaze();
    const night = mode === "night";
    if (this.hoverGlow) (this.hoverGlow.material as THREE.MeshBasicMaterial).opacity = night ? 0.5 : 0.28;
    this.solCoins.setGlowBoost(night ? 1.5 : 1);
  }

  get timeOfDay(): TimeOfDay {
    return this.night.mode;
  }

  /** Billboards (easterEggs.ts) that night lights up: their material gets the night term (call before their first frame). */
  watchBillboards(root: THREE.Object3D): void {
    this.night.patchBillboards(root);
    if (!this.nightSigns.includes(root)) this.nightSigns.push(root);
  }

  /** Night's parts: the lamp lens (measured on the model), the billboards' and the kits' night terms, the fx meshes (hidden by day). */
  private setupNight(): void {
    const lamp = this.streetLamp(1);
    lamp.updateMatrixWorld(true);
    const lens = new THREE.Vector3(), sum = new THREE.Vector3();
    let count = 0;
    lamp.traverse((o) => {
      const mesh = o as THREE.Mesh;
      const uv = mesh.isMesh ? mesh.geometry.getAttribute("uv") as THREE.BufferAttribute | undefined : undefined;
      if (!uv) return;
      const pos = mesh.geometry.getAttribute("position");
      for (let i = 0; i < uv.count; i++) {
        if (Math.floor(uv.getX(i) * 16) !== 8 || Math.floor(uv.getY(i) * 16) !== 10) continue;   // the lens: the kit's white swatch
        sum.add(lens.fromBufferAttribute(pos, i).applyMatrix4(mesh.matrixWorld));
        count++;
      }
    });
    if (count) sum.divideScalar(count); else sum.set(this.lampX - 1.7, 7.6, 0);
    const clouds = (this.clouds.children[0] as THREE.Mesh | undefined)?.material as THREE.Material;
    this.night = new NightMode({
      scene: this.scene, renderer: this.renderer, sun: this.sun, hemi: this.hemi, sky: this.skyMaterial, haze: this.haze, clouds,
      makeEnvironment: (colors) => skyEnvironment(this.renderer, SUN_DIR, colors).texture,
      lens: { x: sum.x, y: sum.y, z: sum.z }, roadEdge: ROAD_HALF, walkEdge: ROAD_HALF + 0.5
    });
    for (const material of this.city?.materials ?? []) this.night.patchKit(material);
    if (this.eggs) this.watchBillboards(this.eggs.root);
    if (this.chaserVehicle) this.night.setChaser(this.chaserVehicle.root);
  }

  /** The car's clear-coat paint and glass (MeshPhysicalMaterial) as plain MeshStandardMaterial on the cheap rungs. */
  private setCheapCar(cheap: boolean): void {
    this.cheapCar = cheap;
    if (!cheap) { this.car.traverse((o) => { const m = o as THREE.Mesh; const back = m.isMesh ? m.userData.physical as THREE.Material | undefined : undefined; if (back) m.material = back; }); return; }
    this.car.traverse((object) => {
      const mesh = object as THREE.Mesh;
      if (!mesh.isMesh || Array.isArray(mesh.material)) return;
      const physical = mesh.material as THREE.MeshPhysicalMaterial;
      if (!physical.isMeshPhysicalMaterial) return;
      let plain = this.carSwap.get(physical);
      if (!plain) {
        plain = new THREE.MeshStandardMaterial({
          color: physical.color, map: physical.map, roughness: physical.roughness, metalness: physical.metalness, envMap: physical.envMap,
          envMapIntensity: physical.envMapIntensity, emissive: physical.emissive, emissiveMap: physical.emissiveMap, emissiveIntensity: physical.emissiveIntensity,
          transparent: physical.transparent, opacity: physical.opacity, side: physical.side, vertexColors: physical.vertexColors,
          normalMap: physical.normalMap, alphaTest: physical.alphaTest, depthWrite: physical.depthWrite
        });
        this.carSwap.set(physical, plain);
      }
      mesh.userData.physical = physical;
      mesh.material = plain;
    });
  }

  /** The sun's orthographic shadow camera, fitted once around SHADOW_BOX as the sun sees it. */
  private setupShadows(size: number): void {
    const shadow = this.sun.shadow;
    if (this.shadowSize !== size) {
      shadow.mapSize.set(size, size);
      shadow.map?.dispose();
      shadow.map = null;
      this.shadowSize = size;
    }
    const { right, up } = this.shadowAxes;
    right.crossVectors(THREE.Object3D.DEFAULT_UP, SUN_DIR).normalize();
    up.crossVectors(SUN_DIR, right).normalize();
    const corner = new THREE.Vector3();
    const box = size <= 1024 ? SHADOW_BOX_TIGHT : SHADOW_BOX;
    const centre = (box.zMin + box.zMax) / 2;
    this.shadowCentre = centre;
    let minX = Infinity, maxX = -Infinity, minY = Infinity, maxY = -Infinity, minZ = Infinity, maxZ = -Infinity;
    for (const x of [-box.x, box.x]) for (const y of [box.yMin, box.yMax]) for (const z of [box.zMin - centre, box.zMax - centre]) {
      corner.set(x, y, z);
      const lx = corner.dot(right), ly = corner.dot(up), lz = corner.dot(SUN_DIR);
      minX = Math.min(minX, lx); maxX = Math.max(maxX, lx);
      minY = Math.min(minY, ly); maxY = Math.max(maxY, ly);
      minZ = Math.min(minZ, lz); maxZ = Math.max(maxZ, lz);
    }
    const camera = shadow.camera;
    camera.left = minX; camera.right = maxX; camera.bottom = minY; camera.top = maxY;
    camera.near = Math.max(0.5, SUN_DISTANCE - maxZ - 4);
    camera.far = SUN_DISTANCE - minZ + 4;
    camera.updateProjectionMatrix();
    this.shadowAxes.texelX = (maxX - minX) / size;
    this.shadowAxes.texelY = (maxY - minY) / size;
    shadow.bias = -0.0005;
    shadow.normalBias = 0.04;
    shadow.intensity = 0.72;                                     // soft daylight shadows: a quarter of the sun still gets in
  }

  /** Keeps the shadow box on the car, moved in whole shadow-map texels so the shadows of still things don't crawl. */
  private followSun(): void {
    const { right, up, texelX, texelY } = this.shadowAxes;
    const focus = this.shadowFocus.set(this.car.position.x, 0, this.car.position.z + this.shadowCentre);   // z ≠ 0 only in the opening
    const rx = focus.dot(right), uy = focus.dot(up);
    focus.addScaledVector(right, Math.round(rx / texelX) * texelX - rx).addScaledVector(up, Math.round(uy / texelY) * texelY - uy);
    this.sun.target.position.copy(focus);
    this.sun.position.copy(focus).addScaledVector(SUN_DIR, SUN_DISTANCE);
  }

  /**
   * The roadster, its board and glows: solid parts cast the sun's shadow (small trim doesn't, it would only cost
   * draws) but take none — the hero stays bright — and the thruster flames are lifted above white once so they bloom.
   */
  private prepareCar(): void {
    const proxy = this.roadster?.shadowProxy;
    const own = new Set<THREE.Object3D>();
    if (proxy?.visible && this.roadster) this.roadster.root.traverse((o) => own.add(o));   // "game" detail: its proxy casts
    this.car.traverse((object) => {
      const mesh = object as THREE.Mesh;
      if (!mesh.isMesh) return;
      if (own.has(mesh)) { mesh.receiveShadow = false; return; }
      const material = (Array.isArray(mesh.material) ? mesh.material[0] : mesh.material) as THREE.Material;
      if (!mesh.geometry.boundingSphere) mesh.geometry.computeBoundingSphere();
      const solid = !material.transparent && material.blending === THREE.NormalBlending;
      mesh.castShadow = solid && (mesh.geometry.boundingSphere?.radius ?? 0) > 0.22;
      mesh.receiveShadow = false;                                // the hero stays bright and readable, even under a truck
    });
    for (const flame of [...(this.roadster?.jets ?? []), ...(this.roadster?.boosters ?? [])]) {
      const material = flame.material as THREE.MeshBasicMaterial;
      if (!material.isMeshBasicMaterial || material.userData.hdr || Math.max(material.color.r, material.color.g, material.color.b) > 1) continue;
      material.color.multiplyScalar(2.2);
      material.userData.hdr = true;
    }
  }

  /** The board's LED skirt ("underglow") breathes while you ride: its colour swings around the design's own. */
  private pulseBoard(): void {
    const glow = this.board?.getObjectByName("underglow") as THREE.Mesh | undefined;
    const material = glow?.material as THREE.MeshBasicMaterial | undefined;
    if (!material?.color) return;
    const base = (material.userData.base ??= material.color.clone()) as THREE.Color;
    material.color.copy(base).multiplyScalar(0.8 + 0.4 * (0.5 + 0.5 * Math.sin(this.clock * 6)));
  }

  /** Lamps, overpasses, the cop: their solid meshes cast the sun's shadow (and take it, if asked); a blob under them shows on low only. */
  private castShadows(root: THREE.Object3D, receive = false): void {
    root.traverse((object) => {
      const mesh = object as THREE.Mesh;
      if (!mesh.isMesh) return;
      if (mesh.name === "blobShadow") { mesh.material = this.lowOnly(mesh.material as THREE.Material); return; }
      const material = (Array.isArray(mesh.material) ? mesh.material[0] : mesh.material) as THREE.Material;
      if (material.transparent || material.blending !== THREE.NormalBlending) return;
      mesh.castShadow = true;
      if (receive) mesh.receiveShadow = true;
    });
  }

  /**
   * Once per new entity model: painted signs and bands become lit surfaces, solid parts cast the sun's shadow and
   * everything takes it, and the soft blob under a solid thing becomes the low-quality fallback. SOL, power-ups and
   * pads keep their glow and their blob.
   */
  private prepareEntity(root: THREE.Object3D, kind: EntityKind): void {
    const solidKind = !NO_SUN_SHADOW.includes(kind);
    const blob = this.materials.get("blob");
    root.updateMatrixWorld(true);
    root.traverse((object) => {
      const mesh = object as THREE.Mesh;
      if (!mesh.isMesh) return;
      if ((blob && mesh.material === blob) || mesh.name === "blobShadow") { if (solidKind) mesh.material = this.lowOnly(mesh.material as THREE.Material); return; }
      if (!solidKind) return;
      const basic = mesh.material as THREE.MeshBasicMaterial;
      if (!Array.isArray(mesh.material) && basic.isMeshBasicMaterial && basic.map && basic.toneMapped && basic.blending === THREE.NormalBlending) mesh.material = this.litDecal(basic);
      const material = (Array.isArray(mesh.material) ? mesh.material[0] : mesh.material) as THREE.Material;
      const flat = mesh.geometry.type === "PlaneGeometry" || mesh.geometry.type === "CircleGeometry";
      if (!mesh.geometry.boundingSphere) mesh.geometry.computeBoundingSphere();
      // wheels, lamps and trim (under ~0.7 m) sit inside the shadow of the body they belong to: they would only cost draws
      const big = (mesh.geometry.boundingSphere?.radius ?? 0) * mesh.matrixWorld.getMaxScaleOnAxis() >= 0.7;
      mesh.castShadow = big && !flat && !material.transparent && material.blending === THREE.NormalBlending;
      mesh.receiveShadow = DRIVABLE.includes(kind);                  // roofs, ramps, decks: where the car's shadow lands
    });
  }

  /** A blob-shadow material that shows on low only (on high the sun's shadow map draws the real one). */
  private lowOnly(material: THREE.Material): THREE.Material {
    if (material === this.materials.get("blob")) return this.blobFallback;
    return this.mat(`low-only:${material.uuid}`, () => {
      const copy = material.clone();
      copy.visible = this.quality !== "high";
      this.fallbacks.push(copy);
      return copy;
    });
  }

  /** `?stats`: what a frame costs (scene + shadow map + post), every two seconds in the console. */
  private logStats(): void {
    const now = performance.now();
    if (now - this.statsAt < 2000) return;
    this.statsAt = now;
    const { render, memory, programs } = this.renderer.info;
    // where the draws come from (?stats=deep only: it walks the whole scene) — renderable objects by owner
    if (this.statsDeep) this.logOwners();
    console.info(`[solrun] quality=${this.quality} scale=${this.resolution.scale} calls=${render.calls} triangles=${render.triangles} geometries=${memory.geometries} textures=${memory.textures} programs=${programs?.length ?? 0}`);
  }

  private logOwners(): void {
    const tally = new Map<string, number>();
    const owner = new Map<THREE.Object3D, string>([[this.car, "car"], [this.chaser, "cop"], [this.clouds, "sky"]]);
    if (this.city) owner.set(this.city.group, "city");
    for (const [, object] of this.entityObjects) owner.set(object, `entity:${(object.userData.key as string ?? "?").split(":")[0]}`);
    this.props.owners(owner);
    const walk = (object: THREE.Object3D, label: string): void => {
      if (!object.visible) return;
      const mine = owner.get(object) ?? label;
      const renderable = (object as THREE.Mesh).isMesh || (object as THREE.Sprite).isSprite || (object as THREE.Line).isLine;
      if (renderable) tally.set(mine, (tally.get(mine) ?? 0) + (Array.isArray((object as THREE.Mesh).material) ? ((object as THREE.Mesh).material as THREE.Material[]).length : 1));
      for (const child of object.children) walk(child, mine);
    };
    for (const child of this.scene.children) walk(child, (child as THREE.Sprite).isSprite ? "particles" : child.type);
    console.info(`[solrun] draws by owner: ${[...tally].sort((a, b) => b[1] - a[1]).map(([k, v]) => `${k} ${v}`).join(" · ")}`);
  }

  // ------------------------------------------------------------------------------------------------ camera

  private resize = (): void => {
    const { clientWidth, clientHeight } = this.host;
    this.camera.aspect = Math.max(0.3, clientWidth / Math.max(1, clientHeight));
    this.camera.fov = this.camera.aspect < 0.8 ? CAM.fov : 58;
    this.camera.updateProjectionMatrix();
    // pixel ratio: up to 1.5 on high, 1.25 on low, times the dynamic render scale (1 → 0.75 when frames run long)
    // desktop: 1.0 (4× MSAA keeps edges stable; native 1.25 cost the owner's iGPU ~4 ms more per frame) and no automatic
    // drops (they only blurred and popped — the frame is CPU-bound there); phones: up to 1.5 / 1.25 with dynamic resolution
    const cap = !this.touch ? 1 : this.quality === "high" ? 1.5 : 1.25;
    const devRatio = import.meta.env.DEV ? Number(new URLSearchParams(location.search).get("dpr")) : 0;   // dev ?dpr=2: crisp promo stills
    const ratio = devRatio > 0 ? Math.min(devRatio, 3) : Math.min(window.devicePixelRatio || 1, cap) * (this.qualityForced || !this.touch ? 1 : this.resolution.scale);
    if (this.renderer.getPixelRatio() !== ratio) this.renderer.setPixelRatio(ratio);
    this.renderer.setSize(clientWidth, clientHeight, false);
    this.post?.setSize(clientWidth, clientHeight, ratio);
  };

  private updateCamera(snapshot: RunSnapshot, dt: number): void {
    const sway = this.settings.reducedMotion ? 0 : Math.sin(this.clock * 1.7) * 0.03;
    const targetX = VX * snapshot.playerX * (snapshot.flying ? CAM.flyingBias : CAM.lateralBias) + sway;
    this.camera.position.x += (targetX - this.camera.position.x) * Math.min(1, dt * CAM.smooth.x);
    const follow = snapshot.flying ? 1 : snapshot.playerY > 3 ? 0.9 : CAM.jumpFollow;
    const pace = this.pace(snapshot);
    // no per-frame random rumble at speed: white noise on the camera reads as choppy frames (speed is felt through the
    // FOV and the speed lines); the crash shake stays, and it is smooth noise, not a new random value every frame
    const shake = this.shake > 0 ? Math.sin(this.clock * 38) * Math.sin(this.clock * 23.7) * this.shake * 0.6 : 0;
    // under a bridge deck the camera stays below it, or the deck's top hides the car and the road (QA-15)
    const targetY = Math.min(CAM.offset.y + PULLBACK.y + snapshot.playerY * follow + shake, this.ceiling);
    this.camera.position.y += (targetY - this.camera.position.y) * Math.min(1, dt * CAM.smooth.y);
    this.camera.position.z = CAM.offset.z + PULLBACK.z;
    this.camera.lookAt(this.camera.position.x * 0.85, this.camera.position.y + CAM.focus.y - 0.6, CAM.focus.z + CAM.offset.z);
    if (this.shake > 0) this.shake = Math.max(0, this.shake - dt * 1.8);
    const base = this.camera.aspect < 0.8 ? CAM.fov : 58;
    const speedy = "jetpack" in snapshot.powerups || "headstart" in snapshot.powerups;
    // the view opens up as the run speeds up (and more on thrusters / boosts), like the original's speed FOV modifier
    const fov = base + pace * 9 + (speedy ? 9 : 0) + (snapshot.boosting ? 6 : 0);
    if (Math.abs(this.camera.fov - fov) > 0.05) { this.camera.fov += (fov - this.camera.fov) * Math.min(1, dt * CAM.fovSmooth); this.camera.updateProjectionMatrix(); }
  }

  private idle(dt: number): void {
    if (this.entityObjects.size || this.coinIds.size || this.live.size) this.syncEntities([], dt);         // the last run's road goes back to the pools
    if (this.deathTime >= 0) this.endDeath();
    this.camera.position.z += (CAM.offset.z + PULLBACK.z - this.camera.position.z) * 0.1;
    this.flipAngle = this.flipTarget = 0;
    this.car.rotation.y += (0 - this.car.rotation.y) * 0.25;
    this.chaser.rotation.y += (0 - this.chaser.rotation.y) * 0.1;
    this.chaser.position.y += (0 - this.chaser.position.y) * 0.1;
    this.scroll(10 * dt);
    this.wheelSpin += dt * 10 / 0.45;
    this.car.position.x += (0 - this.car.position.x) * 0.1;
    this.car.position.y = 0.25 + Math.sin(this.clock * 2) * 0.05;
    this.car.rotation.z = Math.sin(this.clock * 1.3) * 0.02;
    this.car.rotation.x += (0 - this.car.rotation.x) * 0.1;
    this.car.scale.y += (1 - this.car.scale.y) * 0.1;
    this.setRoadsterWheels(false);
    this.setWheels(this.chaserVehicle, false);
    if (this.roadster) { this.roadster.body.rotation.set(0, 0, Math.sin(this.clock * 1.3) * 0.02); this.setJets(0.15, 0.5); }
    this.chaser.position.z += (-10 - this.chaser.position.z) * 0.05;
    this.chaser.position.x += (0 - this.chaser.position.x) * 0.05;
    this.chaser.visible = true;
    this.camera.position.x += (0 - this.camera.position.x) * 0.05;
    this.camera.position.y += (CAM.offset.y + PULLBACK.y - this.camera.position.y) * 0.05;
    this.camera.lookAt(0, this.camera.position.y + CAM.focus.y - 0.6, CAM.focus.z + CAM.offset.z);
    if (this.carShadow) this.carShadow.position.y = -this.car.position.y + 0.02;
    this.flashChaser(false);
  }

  // ------------------------------------------------------------------------------------------------ vehicles

  /** A Kenney car fitted to `width`, wheels found by name so they can spin and fold. */
  private vehicle(model: string, width: number): Vehicle {
    const root = this.assets.fit(model, { x: width });
    const wheels: THREE.Object3D[] = [];
    root.traverse((o) => { if (o.name.startsWith("wheel")) { wheels.push(o); o.userData.baseRotation = o.rotation.clone(); } });
    const box = new THREE.Box3().setFromObject(root);
    return { root, wheels, length: box.max.z - box.min.z, height: box.max.y - box.min.y };
  }

  /** Rolling on the road; folded flat as hover pads in the air (it is a flying car). */
  private setWheels(vehicle: Vehicle | undefined, flying: boolean): void {
    if (!vehicle) return;
    for (const wheel of vehicle.wheels) {
      const base = wheel.userData.baseRotation as THREE.Euler;
      const left = wheel.name.includes("left");
      const targetZ = flying ? (left ? -Math.PI / 2 : Math.PI / 2) : 0;
      wheel.rotation.z += (base.z + targetZ - wheel.rotation.z) * 0.25;
      wheel.rotation.x = flying ? base.x : base.x + this.wheelSpin;
    }
  }

  private setRoadsterWheels(folded: boolean): void {
    if (!this.roadster) return;
    for (const wheel of this.roadster.wheels) {
      const left = wheel.name.includes("left");
      const targetZ = folded ? (left ? -Math.PI / 2 : Math.PI / 2) : 0;
      wheel.rotation.z += (targetZ - wheel.rotation.z) * 0.25;
      wheel.rotation.x = folded ? 0 : this.wheelSpin;
    }
  }

  /** Thrusters: `down` = the four jets under the car, `back` = the boosters. 0 = off, 1 = full. */
  private setJets(down: number, back: number): void {
    if (!this.roadster) return;
    for (const jet of this.roadster.jets) {
      const flicker = 1 + Math.sin(this.clock * 47 + jet.id) * 0.12;
      jet.scale.set(1, Math.max(0.001, down * flicker * 1.4), 1);
      jet.visible = down > 0.02;
    }
    for (const flame of this.roadster.boosters) {
      const flicker = 1 + Math.sin(this.clock * 41 + flame.id) * 0.1;
      flame.scale.set(1, Math.max(0.001, back * flicker), 1);
    }
  }

  private updateCar(snapshot: RunSnapshot, dt: number): void {
    this.wheelSpin += dt * snapshot.speed / 0.38;
    const dx = VX * snapshot.playerX - this.car.position.x;
    this.car.position.x += dx * Math.min(1, dt * 18);
    // the kickflip: each lane change adds a full roll in its direction (right on screen = a roll to the right)
    if (snapshot.lane !== this.lastLane) {
      const dir = Math.sign(snapshot.lane - this.lastLane);
      if (!this.settings.reducedMotion) this.flipTarget += dir * Math.PI * 2;
      this.lastLane = snapshot.lane;
      this.smokeTimer = FLIP_SECONDS;
    }
    const remaining = this.flipTarget - this.flipAngle;
    const step = (Math.PI * 2 / FLIP_SECONDS) * dt;
    this.flipAngle = Math.abs(remaining) <= step ? this.flipTarget : this.flipAngle + Math.sign(remaining) * step;
    const flipping = Math.abs(this.flipTarget - this.flipAngle) > 0.01;
    const progress = flipping ? 1 - Math.min(1, Math.abs(this.flipTarget - this.flipAngle) / (Math.PI * 2)) : 1;
    const lift = flipping ? Math.sin(Math.PI * progress) * FLIP_LIFT : 0;
    const flying = snapshot.flying || snapshot.airborne;
    const hover = snapshot.flying ? Math.sin(this.clock * 3) * 0.12 : Math.sin(this.clock * 8) * 0.025;
    this.car.position.y = 0.2 + snapshot.playerY + hover + lift;
    const bank = flipping ? 0 : THREE.MathUtils.clamp(-(VX * snapshot.lane * LANE - this.car.position.x) * 0.12, -0.3, 0.3);
    const pitch = snapshot.flying ? -0.06 : THREE.MathUtils.clamp(-snapshot.vy * 0.012, -0.25, 0.25);
    if (this.roadster) {
      const body = this.roadster.body;
      body.rotation.z = this.flipAngle + bank;
      body.rotation.x += (pitch - body.rotation.x) * Math.min(1, dt * 10);
      body.position.y = 0;
      const duck = snapshot.ducking ? 0.62 : 1;
      body.scale.y += (duck - body.scale.y) * Math.min(1, dt * 14);
    }
    if (!flipping && Math.abs(this.flipAngle) > Math.PI * 64) { this.flipAngle %= Math.PI * 2; this.flipTarget = this.flipAngle; }
    this.setRoadsterWheels(flying || flipping);
    // the hover board: the car rides on it; when it breaks, it bursts
    const onBoard = snapshot.boardLeft > 0;
    if (this.board) {
      this.board.visible = onBoard;
      if (onBoard) {
        this.board.position.y = -0.05 + Math.sin(this.clock * 10) * 0.03;
        animateProp(this.board, this.clock);                         // its underlight breathes
        this.pulseBoard();
      }
    }
    if (this.lastBoard > 0.2 && !onBoard) this.burst(this.car.position.x, this.car.position.y + 0.2, 0, 0x14f195, 22, 6);
    this.lastBoard = snapshot.boardLeft;
    if (onBoard) this.car.position.y += 0.35;
    const headstart = "headstart" in snapshot.powerups;
    const down = snapshot.flying ? 1 : flipping ? 1 : snapshot.airborne ? (snapshot.vy > 0 ? 0.9 : 0.35) : onBoard ? 0.5 : 0.12;
    const back = headstart ? 2.4 : snapshot.flying ? 1.4 : snapshot.boosting ? 1.6 : 0.45 + this.pace(snapshot) * 0.5;
    if (headstart && Math.random() < 0.9) this.burst(this.car.position.x, this.car.position.y + 0.4, -3, 0x9fe8ff, 1, 2, true);
    this.setJets(down, back);
    if (this.hoverGlow) (this.hoverGlow.material as THREE.MeshBasicMaterial).opacity = flipping ? 0 : flying ? 0.55 : 0.28 + Math.sin(this.clock * 6) * 0.05;
    if (this.shieldGlow) this.shieldGlow.visible = snapshot.protected && (Math.floor(this.clock * 10) % 4 !== 0);
    if (this.carShadow) {
      const ground = this.ground;                                   // the surface below, not the car's own height (QA-22)
      const height = Math.max(0, snapshot.playerY + lift - ground);
      this.carShadow.position.y = -this.car.position.y + ground + 0.03;
      const s = Math.max(0.35, 1 - height * 0.07);
      this.carShadow.scale.set(s, s, 1);
    }
  }

  /** 0 at the run's start speed, 1 at its top speed. */
  private pace(snapshot: RunSnapshot): number {
    return THREE.MathUtils.clamp((snapshot.speed - M.startSpeed) / (M.endSpeed - M.startSpeed), 0, 1.4);
  }

  /** The GAS COP cruiser (props/cop.ts): light bar, ETH crest and contact shadow on board; wheels roll like vehicle()'s. */
  private buildChaser(): void {
    const cop = buildGasCop();
    for (const wheel of cop.wheels) wheel.userData.baseRotation = wheel.rotation.clone();
    const vehicle: Vehicle = { root: cop.root, wheels: cop.wheels, length: cop.length, height: cop.height };
    this.chaserVehicle = vehicle;
    this.chaser.add(vehicle.root);
    this.chaser.position.set(0, 0, -10);
    this.castShadows(vehicle.root);
    this.scene.add(this.chaser);
  }

  /** The cop keeps `chaserDistance` between his front bumper and your rear. */
  private updateChaser(snapshot: RunSnapshot, dt: number): void {
    const carLength = this.roadster?.length ?? 4.4;
    const copLength = this.chaserVehicle?.length ?? 4.0;
    const targetZ = -(snapshot.chaserDistance + carLength / 2 + copLength / 2);
    this.chaser.position.z += (targetZ - this.chaser.position.z) * Math.min(1, dt * 6);
    this.chaser.position.x += (VX * snapshot.chaserX - this.chaser.position.x) * Math.min(1, dt * 8);
    this.chaser.position.y = snapshot.chaserY + Math.sin(this.clock * 9) * 0.02;
    const yaw = THREE.MathUtils.clamp((this.car.position.x - this.chaser.position.x) * 0.06, -0.25, 0.25);
    this.chaser.rotation.y += (yaw - this.chaser.rotation.y) * Math.min(1, dt * 10);   // eased: after a continue it turns back from its stop
    // never into the car (caught.ts): after a continue it comes back from beside you, and the lane and gap smoothing could cut the corner
    const clear = keepClear({ x: this.chaser.position.x, z: this.chaser.position.z, yaw: this.chaser.rotation.y }, { x: this.car.position.x, z: 0, yaw: this.car.rotation.y });
    this.chaser.position.x = clear.x;
    this.chaser.position.z = clear.z;
    this.chaser.visible = this.chaser.position.z > -45;
    this.setWheels(this.chaserVehicle, false);
    this.flashChaser(snapshot.hunted);
  }

  /** The light bar: a double strobe, faster while he hunts you (animateProp → animateGasCop). */
  private flashChaser(fast: boolean): void {
    if (!this.chaserVehicle) return;
    this.chaserVehicle.root.userData.fast = fast;
    animateProp(this.chaserVehicle.root, this.clock);
  }

  private effects(snapshot: RunSnapshot, dt: number): void {
    if (snapshot.coins > this.lastCoins) this.burst(this.car.position.x, this.car.position.y + 0.8, 1.2, 0xc9a7ff, 7, 3.5);
    this.lastCoins = snapshot.coins;
    if (snapshot.hunted && !this.lastHunted) this.burst(this.car.position.x, this.car.position.y + 0.4, 0, 0xffffff, 14, 4);
    this.lastHunted = snapshot.hunted;
    if (this.wasAirborne && !snapshot.airborne && !snapshot.flying) this.burst(this.car.position.x, snapshot.supportHeight + 0.1, -1.2, 0xe8e2f2, 10, 2.5, true);
    this.wasAirborne = snapshot.airborne;
    // thruster smoke while flipping, hopping up or flying (the video's white plumes)
    this.smokeTimer = Math.max(0, this.smokeTimer - dt);
    const puffing = this.smokeTimer > 0 || snapshot.flying || (snapshot.airborne && snapshot.vy > 3);
    if (puffing && Math.random() < 0.35) this.burst(this.car.position.x + (Math.random() - 0.5) * 1.2, this.car.position.y - 0.1, (Math.random() - 0.5) * 2, 0xffffff, 1, 1.4, true);
  }

  // ------------------------------------------------------------------------------------------------ particles

  /** Particles: `drift` = their speed along the road (default: blown back a little); `scale` grows the puffs. */
  private burst(x: number, y: number, z: number, color: number, count: number, speed: number, soft = false, drift?: number, scale = 1): void {
    if (this.settings.reducedMotion) return;
    for (let i = 0; i < count; i++) {
      // a soft round puff (the glow texture), not a flat square — squares read as blocks, worst at night (2026-10-17)
      const sprite = this.particlePool.pop() ?? new THREE.Sprite(new THREE.SpriteMaterial({ map: this.glowTexture(), color, transparent: true, depthWrite: false }));
      const material = sprite.material as THREE.SpriteMaterial;
      material.color.set(color);
      if (soft && this.night.mode === "night") material.color.multiplyScalar(0.3);   // unlit smoke would glow white at night
      material.opacity = soft ? 0.55 : 1;
      sprite.position.set(x + (Math.random() - 0.5) * 0.6 * scale, y, z);
      const size = (soft ? 0.8 + Math.random() * 0.8 : 0.24 + Math.random() * 0.22) * scale;   // the round falloff shows ~60 % of the quad: a bit bigger than the old squares
      sprite.scale.set(size, size, 1);
      this.scene.add(sprite);
      this.particles.push({ sprite, vx: (Math.random() - 0.5) * speed, vy: Math.random() * speed * (soft ? 0.4 : 1), vz: (Math.random() - 0.5) * speed + (drift ?? -(soft ? 6 : 2)), life: 0, total: soft ? 0.45 : 0.5 + Math.random() * 0.3 });
    }
  }

  private updateParticles(dt: number): void {
    for (let i = this.particles.length - 1; i >= 0; i--) {
      const p = this.particles[i];
      p.life += dt;
      p.sprite.position.x += p.vx * dt;
      p.sprite.position.y += p.vy * dt;
      p.sprite.position.z += p.vz * dt;
      p.vy -= 9 * dt;
      (p.sprite.material as THREE.SpriteMaterial).opacity = Math.max(0, 1 - p.life / p.total);
      if (p.life >= p.total) {
        this.scene.remove(p.sprite);
        this.particlePool.push(p.sprite);
        this.particles.splice(i, 1);
      }
    }
  }

  // ------------------------------------------------------------------------------------------------ speed

  /** Speed stripes: thin streaks rushing past, stronger the faster you go (the original's speed stripes on boosts). */
  private buildSpeedLines(): void {
    const material = new THREE.MeshBasicMaterial({ color: 0xffffff, transparent: true, opacity: 0, depthWrite: false, blending: THREE.AdditiveBlending });
    const geometry = new THREE.BoxGeometry(0.05, 0.05, 7);
    for (let i = 0; i < 46; i++) {
      const line = new THREE.Mesh(geometry, material);           // a placeholder that carries the position; drawn by the batch below
      this.placeSpeedLine(line, true);
      this.speedLines.push(line);
    }
    this.speedLineBatch = new THREE.InstancedMesh(geometry, material, this.speedLines.length);   // one draw for all of them
    this.speedLineBatch.frustumCulled = false;
    this.speedLineBatch.visible = false;
    this.scene.add(this.speedLineBatch);
  }

  private placeSpeedLine(line: THREE.Mesh, anywhere: boolean): void {
    const angle = Math.random() * Math.PI * 2;
    const radius = 4.5 + Math.random() * 7;
    line.position.set(Math.cos(angle) * radius, 3 + Math.sin(angle) * radius * 0.6 + 2.5, anywhere ? Math.random() * 60 : 50 + Math.random() * 20);
  }

  private updateSpeedLines(snapshot: RunSnapshot, dt: number): void {
    const pace = this.pace(snapshot);
    const boost = ("jetpack" in snapshot.powerups || "headstart" in snapshot.powerups ? 1 : 0) + (snapshot.boosting ? 0.8 : 0);
    // only while boosting (thrusters, headstart, speed pads) — the original's SpeedStripeOwner; at plain speed they read as
    // metal bars flying over the road (the owner, 2026-10-16). Speed is felt through the FOV.
    void pace;
    const strength = snapshot.state === "dead" || this.settings.reducedMotion ? 0 : Math.min(1, boost * 0.8);
    const material = this.speedLines[0]?.material as THREE.MeshBasicMaterial | undefined;
    if (material) material.opacity += (strength * 0.4 - material.opacity) * Math.min(1, dt * 4);
    for (const line of this.speedLines) {
      line.visible = (material?.opacity ?? 0) > 0.01;
      line.position.z -= snapshot.speed * 2.2 * dt;
      line.position.x += (this.camera.position.x - line.position.x) * 0;
      if (line.position.z < -12) this.placeSpeedLine(line, false);
    }
    const batch = this.speedLineBatch;
    if (batch) {
      batch.visible = (material?.opacity ?? 0) > 0.01;
      if (batch.visible) {
        this.speedLines.forEach((line, i) => { line.updateMatrix(); batch.setMatrixAt(i, line.matrix); });
        batch.instanceMatrix.needsUpdate = true;
      }
    }
  }

  // ------------------------------------------------------------------------------------------------ caught

  /**
   * The end of a run: the car crashes and bounces, ELON's pickup pulls out into the free lane beside you and drifts to
   * a stop there, nose angled in, never touching the car (caught.ts); the camera swings round from the other side.
   */
  private startDeath(entities: WorldEntity[]): void {
    this.deathTime = 0;
    const reduced = this.settings.reducedMotion;
    // the side: the free neighbouring lane (anything solid at the pickup's height along its way counts as taken)
    const obstacles: Area[] = [];
    for (const e of entities) {
      if (!e.active || e.yBase > 2.2 || !(DRIVABLE.includes(e.kind) || DANGER_KINDS.includes(e.kind))) continue;
      const x = VX * e.x, z = e.z - this.carZ;
      obstacles.push({ x0: x - e.width / 2, x1: x + e.width / 2, z0: z, z1: z + e.depth });
    }
    const carX = this.car.position.x;
    this.deathSide = pickSide({ x: carX, z: 0, yaw: 0 }, ROAD_HALF, obstacles, this.deathAlternate, reduced);
    this.deathAlternate = this.deathAlternate === 1 ? -1 : 1;
    this.deathRest = restPose({ x: carX, z: 0, yaw: this.deathSide * CAR_YAW }, this.deathSide, reduced);
    this.deathFrom = { x: this.chaser.position.x, z: this.chaser.position.z, yaw: this.chaser.rotation.y };
    this.deathFromY = this.chaser.position.y;
    // the camera: from the side away from the pickup (it stops beyond the car, not in front of the lens), and no further
    // out than a metre past the curb (lamps and billboards stand on the sidewalk)
    const lookX = THREE.MathUtils.lerp(carX, this.deathRest.x, this.deathLook);
    const room = (ROAD_HALF + 1 + this.deathSide * lookX) / this.deathRadius;
    this.deathSwing = -this.deathSide * Math.min(0.9, Math.asin(THREE.MathUtils.clamp(room, 0, 1)));
    this.flipTarget = this.flipAngle = 0;
    this.burst(this.car.position.x, this.car.position.y + 0.8, 1.5, 0xffd27a, 18, 6);
    this.burst(this.car.position.x, this.car.position.y + 0.6, 1.0, 0xffffff, 10, 3, true);
  }

  private endDeath(): void {
    this.deathTime = -1;
    if (this.roadster) this.roadster.body.rotation.set(0, 0, 0);
    this.car.rotation.set(0, 0, 0);
    this.chaser.rotation.x = this.chaser.rotation.z = 0;            // no roll left over; the chase (or the menu) turns it straight
    this.steerChaser(0);
  }

  private updateDeath(snapshot: RunSnapshot, dt: number): void {
    this.deathTime += dt;
    // dev ?catchHold=<ms>: jump straight to that moment and hold it (headless screenshots get too few frames to play it through)
    if (import.meta.env.DEV && this.devCatchHold > 0) this.deathTime = this.devCatchHold;
    const t = this.deathTime;
    // the car: knocked back, spun a little (nose toward the pickup's side, so its tail swings away from the stop), settles
    const knock = Math.min(1, t / 0.35);
    this.car.position.y = 0.2 + snapshot.playerY + Math.sin(Math.min(1, t / 0.45) * Math.PI) * 0.8;
    this.car.rotation.y = this.deathSide * CAR_YAW * knock;
    if (this.roadster) {
      this.roadster.body.rotation.z += (this.deathSide * 0.25 - this.roadster.body.rotation.z) * Math.min(1, dt * 6);
      this.setJets(t < 0.3 ? 0.6 : 0, 0);
    }
    if (t < 0.6 && Math.random() < 0.5) this.burst(this.car.position.x, this.car.position.y + 0.5, 0, 0x9a98b8, 1, 1.5, true);
    // the pickup: out into the free lane, a drift (tail out, a countersteer catches it), stopped beside you nose in —
    // on the road whatever the car is up on, and kept clear of this frame's car (caught.ts)
    const frame = caughtPose(t, this.deathFrom, this.deathRest, this.deathSide, this.settings.reducedMotion);
    const pose = keepClear(frame, { x: this.car.position.x, z: 0, yaw: this.car.rotation.y });
    this.chaser.visible = true;
    this.chaser.position.set(pose.x, frame.lift + this.deathFromY * Math.max(0, 1 - (t / 0.22) ** 2), pose.z);
    this.chaser.rotation.set(0, pose.yaw, frame.roll);
    this.setWheels(this.chaserVehicle, false);
    this.steerChaser(frame.steer);
    this.flashChaser(true);
    if (frame.smoke > 0) this.tyreSmoke(pose, frame.smoke);
    // the camera swings round (from the side away from the pickup) and drops low, looking between the car and the stop
    const swing = Math.min(1, t / 1.1) * this.deathSwing;
    const target = this.deathTarget.set(THREE.MathUtils.lerp(this.car.position.x, this.deathRest.x, this.deathLook), this.car.position.y + 0.8, this.deathRest.z * this.deathLook);
    const desired = this.deathEye.set(target.x + Math.sin(swing) * this.deathRadius, target.y + 3.2, target.z - Math.cos(swing) * this.deathRadius);
    this.camera.position.lerp(desired, Math.min(1, dt * 3));
    this.camera.lookAt(target);
  }

  /** Tyre smoke off the pickup's rear wheels while it slides: the scene's soft puffs, rising and left hanging where they formed. */
  private tyreSmoke(pose: Pose2, strength: number): void {
    if (Math.random() > 0.85 * strength) return;
    const c = Math.cos(pose.yaw), s = Math.sin(pose.yaw);
    for (const lx of [-0.85, 0.85]) {
      const lz = -1.6;                                             // the rear axle
      const first = this.particles.length;
      this.burst(pose.x + lx * c + lz * s, 0.3, pose.z - lx * s + lz * c, 0xe9e6f0, 1, 1.4, true);
      for (let i = first; i < this.particles.length; i++) {
        const p = this.particles[i];
        p.vz *= 0.12;
        p.vy = 2.4 + Math.random();
        p.total = 0.75;
        p.sprite.scale.multiplyScalar(1.5);
      }
    }
  }

  /** The pickup's front wheels turned by `angle` on top of their roll (rotation order YXZ: the steer outermost). */
  private steerChaser(angle: number): void {
    for (const wheel of this.chaserVehicle?.wheels ?? []) {
      if (!wheel.name.includes("front")) continue;
      wheel.rotation.order = "YXZ";
      wheel.rotation.y = angle;
    }
  }

  /** dev `?catch`: a running snapshot turned into a catch once the run is that old (the engine plays on unless ?freeze stops it). */
  private devCaught(snapshot: RunSnapshot, dt: number): RunSnapshot {
    if (snapshot.state === "dead" || snapshot.idle) return snapshot;
    this.devRunTime += dt;
    return this.devRunTime * 1000 >= this.devCatch ? { ...snapshot, state: "dead", caught: true, hunted: false } : snapshot;
  }

  // ------------------------------------------------------------------------------------------------ the city

  private mat(key: string, make: () => THREE.Material): THREE.Material {
    let m = this.materials.get(key);
    if (!m) { m = make(); this.materials.set(key, m); }
    return m;
  }

  /** Matte paint: lit by the sun, the sky light and the environment map; shadowed on high. */
  private flat(color: number): THREE.Material {
    return this.mat(`flat:${color}`, () => new THREE.MeshLambertMaterial({ color }));
  }

  /** A light: unlit and brighter than white (HDR), so it blooms on high. */
  private glow(color: number, strength: number): THREE.MeshBasicMaterial {
    return this.mat(`glow:${color}:${strength}`, () => new THREE.MeshBasicMaterial({ color: new THREE.Color(color).multiplyScalar(strength) })) as THREE.MeshBasicMaterial;
  }

  /** Polished metal (the key): mirrors the sky in the environment map. */
  private metal(color: number): THREE.Material {
    return this.mat(`metal:${color}`, () => new THREE.MeshStandardMaterial({ color, roughness: 0.3, metalness: 0.9 }));
  }

  /** A painted sign, band or decal (an unlit MeshBasicMaterial in the entity code) as a lit, shadowed surface. */
  private litDecal(source: THREE.MeshBasicMaterial): THREE.Material {
    const map = source.map;
    return this.mat(`decal:${map?.uuid}:${source.color.getHexString()}:${source.side}:${source.transparent}:${source.alphaTest}`, () => new THREE.MeshStandardMaterial({
      map, color: source.color, side: source.side, transparent: source.transparent, alphaTest: source.alphaTest, opacity: source.opacity,
      roughness: 0.6, metalness: 0, emissive: 0xffffff, emissiveMap: map, emissiveIntensity: 0.14
    }));
  }

  /** The asphalt with the markings painted in (anisotropic: it is seen at a grazing angle). */
  private roadMaterial(): THREE.Material {
    return this.mat("road", () => {
      const map = this.tex("road", () => roadTexture(ROAD_HALF * 2, SEGMENT, LANE));
      map.anisotropy = Math.min(16, this.renderer.capabilities.getMaxAnisotropy());
      map.wrapT = THREE.RepeatWrapping;
      map.repeat.set(1, LOOP / SEGMENT);
      this.streetMaps.push(map);
      return new THREE.MeshLambertMaterial({ map });
    });
  }

  private pavingMaterial(): THREE.Material {
    return this.mat("paving", () => {
      const map = this.tex("paving", () => pavingTexture(7.5, SEGMENT));
      map.anisotropy = Math.min(8, this.renderer.capabilities.getMaxAnisotropy());
      map.wrapT = THREE.RepeatWrapping;
      map.repeat.set(1, LOOP / SEGMENT);
      this.streetMaps.push(map);
      return new THREE.MeshLambertMaterial({ map });
    });
  }

  /** The soft halo behind a power-up, in its colour. */
  private pickupGlow(color: string): THREE.Sprite {
    const glow = new THREE.Sprite(this.mat(`pickup-glow:${color}`, () => new THREE.SpriteMaterial({ map: this.glowTexture(), color: new THREE.Color(color).multiplyScalar(1.6), transparent: true, depthWrite: false, blending: THREE.AdditiveBlending })) as THREE.SpriteMaterial);
    glow.scale.set(1.9, 1.9, 1);
    return glow;
  }

  /** Bakes a static group into one mesh per material (one draw call each); hands the group back if it can't. */
  private merged(group: THREE.Group): THREE.Object3D {
    group.updateMatrixWorld(true);
    const byMaterial = new Map<THREE.Material, THREE.BufferGeometry[]>();
    let mergeable = true;
    group.traverse((object) => {
      const mesh = object as THREE.Mesh;
      if (!mesh.isMesh) return;
      if (Array.isArray(mesh.material)) { mergeable = false; return; }
      const list = byMaterial.get(mesh.material) ?? [];
      list.push(mesh.geometry.clone().applyMatrix4(mesh.matrixWorld));
      byMaterial.set(mesh.material, list);
    });
    if (!mergeable) return group;
    const out = new THREE.Group();
    for (const [material, list] of byMaterial) {
      const geometry = mergeGeometries(list);
      list.forEach((g) => g.dispose());
      if (!geometry) return group;
      out.add(new THREE.Mesh(geometry, material));
    }
    return out;
  }

  private geometries = new Map<string, THREE.BufferGeometry>();

  /** Shared box geometry: the city re-dresses every 12 m, so nothing may be created per segment. */
  private box(w: number, h: number, d: number): THREE.BufferGeometry {
    const key = `${w}x${h}x${d}`;
    let g = this.geometries.get(key);
    if (!g) { g = new THREE.BoxGeometry(w, h, d); this.geometries.set(key, g); }
    return g;
  }

  private buildWorld(): void {
    this.scene.background = new THREE.Color(HAZE);
    this.scene.fog = new THREE.Fog(HAZE, 110, 560);
    // light: the sun (it casts the shadows on high), sky above and a warm bounce below, and the sky itself as an
    // environment map so paint, glass and the kits' materials reflect it
    this.scene.add(this.hemi);
    this.sun.position.copy(SUN_DIR).multiplyScalar(SUN_DISTANCE);
    this.scene.add(this.sun, this.sun.target);
    this.environment = skyEnvironment(this.renderer, SUN_DIR, {
      top: new THREE.Color(SKY_TOP), horizon: new THREE.Color(HAZE), ground: new THREE.Color(0xa49caa), sun: new THREE.Color(0xfff0dc).multiplyScalar(4)
    });
    this.scene.environment = this.environment.texture;
    this.scene.environmentIntensity = 0.3;                         // the PBR props: reflections; the sky light does the ambient

    // the sky: deep blue overhead, a pale haze band where it meets the city (the fog's colour), the sun with a soft glow
    const skyMaterial = new THREE.ShaderMaterial({
      side: THREE.BackSide, depthWrite: false, fog: false,
      uniforms: {
        top: { value: new THREE.Color(SKY_TOP) }, horizon: { value: new THREE.Color(SKY_HORIZON) }, haze: { value: this.haze },
        sunColor: { value: new THREE.Color(0xfff3dc) }, sunDirection: { value: SKY_SUN.clone() }, night: { value: 0 }
      },
      vertexShader: "varying vec3 vDir; void main(){ vec4 world = modelMatrix * vec4(position, 1.0); vDir = world.xyz - cameraPosition; gl_Position = projectionMatrix * viewMatrix * world; }",
      fragmentShader: `
        uniform vec3 top; uniform vec3 horizon; uniform vec3 haze; uniform vec3 sunColor; uniform vec3 sunDirection; uniform float night;
        varying vec3 vDir;
        void main() {
          vec3 d = normalize(vDir);
          float h = clamp(d.y, 0.0, 1.0);
          vec3 color = mix(horizon, top, pow(h, 0.55));
          color = mix(color, haze, exp(-h * 9.0));
          float s = max(dot(d, sunDirection), 0.0);
          color += sunColor * (smoothstep(0.99935, 0.99965, s) * 7.0 + pow(s, 220.0) * 1.1 + pow(s, 12.0) * 0.22);
          if (night > 0.5) {
            // night (night.ts): the sun term above is the moon; stars, one in ~70 cells of a fine grid on the sky, above the haze
            vec3 q = d * 240.0;
            vec3 cell = floor(q);
            float star = fract(sin(dot(cell, vec3(127.1, 311.7, 74.7))) * 43758.5453);
            if (star > 0.986) {
              float r = length(fract(q) - 0.5);
              color += vec3(0.82, 0.88, 1.0) * smoothstep(0.34, 0.0, r) * (0.5 + (star - 0.986) * 80.0) * smoothstep(0.04, 0.32, d.y);
            }
          }
          gl_FragColor = vec4(color, 1.0);
          #include <tonemapping_fragment>
          #include <colorspace_fragment>
        }`
    });
    this.skyMaterial = skyMaterial;
    const sky = new THREE.Mesh(new THREE.SphereGeometry(620, 32, 16, 0, Math.PI * 2, 0, Math.PI / 2), skyMaterial);
    sky.position.y = -30;
    sky.renderOrder = 1;                                         // after the city: only the pixels left over are shaded
    this.scene.add(sky);
    // clouds: every puff merged into one mesh, softly lit (a sunny side, a bluish belly)
    const puffs: THREE.BufferGeometry[] = [];
    for (let i = 0; i < 16; i++) {
      const count = 3 + Math.floor(Math.random() * 3);
      const cx = (Math.random() - 0.5) * 620, cy = 110 + Math.random() * 80, cz = 220 + Math.random() * 300;
      for (let j = 0; j < count; j++) {
        const puff = new THREE.SphereGeometry(8 + Math.random() * 7, 12, 8);
        puff.scale(1, 0.55, 1);
        puff.translate(cx + j * 10 - count * 4.5, cy + Math.random() * 3, cz + Math.random() * 3);
        puffs.push(puff);
      }
    }
    const cloudGeometry = mergeGeometries(puffs);
    puffs.forEach((g) => g.dispose());
    if (cloudGeometry) this.clouds.add(new THREE.Mesh(cloudGeometry, new THREE.MeshLambertMaterial({ color: 0xffffff, emissive: 0xa9b8d6, emissiveIntensity: 0.4, fog: false })));
    this.scene.add(this.clouds);

    const ground = new THREE.Mesh(new THREE.PlaneGeometry(1400, 1400), this.flat(0xb8b2d6));
    ground.rotation.x = -Math.PI / 2;
    ground.position.set(0, -0.06, 300);
    this.scene.add(ground);
    // the street under the slices: road, curbs and sidewalks as one long mesh each (three draws instead of five per
    // 12 m slice); their painted textures slide with the city in scroll()
    const streetZ = -SEGMENT / 2 - 36 + LOOP / 2;
    const road = new THREE.Mesh(new THREE.PlaneGeometry(ROAD_HALF * 2, LOOP).rotateX(-Math.PI / 2), this.roadMaterial());
    const curbs = new THREE.Mesh(mergeGeometries([-1, 1].map((side) => new THREE.BoxGeometry(0.5, 0.4, LOOP).translate(side * (ROAD_HALF + 0.25), 0.2, 0))), this.flat(0xb6b4c8));
    const walks = new THREE.Mesh(mergeGeometries([-1, 1].map((side) => new THREE.BoxGeometry(7.5, 0.34, LOOP).translate(side * (ROAD_HALF + 4.25), 0.17, 0))), this.pavingMaterial());
    for (const part of [road, curbs, walks]) {
      part.position.z = streetZ;
      part.receiveShadow = part === road;                          // only the road takes shadows (per-pixel cost)
      this.scene.add(part);
    }
    for (const fixed of [ground, road, curbs, walks, this.skyline]) { fixed?.updateMatrix(); if (fixed) fixed.matrixAutoUpdate = false; }
    // the far skyline: low-detail blocks, baked into one mesh (one draw call instead of ~50)
    const skyline = new THREE.Group();
    for (let i = 0; i < 46; i++) {
      const key = FAR_BUILDINGS[i % FAR_BUILDINGS.length];
      const s = CITY * (1.4 + Math.random() * 1.4);
      const tower = this.assets.clone(key);
      tower.scale.setScalar(s);
      const side = i % 2 ? 1 : -1;
      tower.position.set(side * (70 + Math.random() * 150), 0, 140 + Math.random() * 360);
      tower.rotation.y = Math.floor(Math.random() * 4) * Math.PI / 2;
      skyline.add(tower);
    }
    this.skyline = this.merged(skyline);
    this.scene.add(this.skyline);

    this.city = this.cityBatches();
    this.scene.add(this.city.group);
    for (let i = 0; i < SEGMENTS; i++) {
      const segment = new THREE.Group();                           // a scratch group: decorate() dresses it, the batches take it
      segment.userData.cz = i * SEGMENT - 36;
      this.segments.push(segment);
      this.dress(segment);
    }
  }

  /** (Re)builds one 12 m slice of city: road, curbs, sidewalks, lamps, a building or a park each side, the red bridge comes from the road itself. */
  private decorate(segment: THREE.Group): void {
    segment.clear();
    const n = this.segmentCounter++;
    segment.userData.lamp = n % 2 === 0;                         // signSpot() stands billboards just in front of these
    const industrial = Math.floor(n / 40) % 3 === 2;
    for (const side of [-1, 1]) {
      if (n % 2 === 0) {
        const lamp = this.streetLamp(side);
        this.castShadows(lamp);
        segment.add(lamp);
      }
      const roll = this.random();
      const baseX = side * (ROAD_HALF + 8.5);
      if (industrial) {
        const key = INDUSTRY[Math.floor(this.random() * INDUSTRY.length)];
        const building = this.assets.clone(key);
        building.scale.setScalar(CITY * 0.9);
        building.rotation.y = side > 0 ? -Math.PI / 2 : Math.PI / 2;
        building.position.set(baseX + side * this.frontOffset(key, CITY * 0.9), 0, 0);
        segment.add(building);
      } else if (roll < 0.62) {
        const key = MID_BUILDINGS[Math.floor(this.random() * MID_BUILDINGS.length)];
        const building = this.assets.clone(key);
        building.scale.setScalar(CITY);
        building.rotation.y = side > 0 ? -Math.PI / 2 : Math.PI / 2;
        building.position.set(baseX + side * this.frontOffset(key, CITY), 0, 0);
        segment.add(building);
      } else if (roll < 0.8) {
        const key = TALL_BUILDINGS[Math.floor(this.random() * TALL_BUILDINGS.length)];
        const building = this.assets.clone(key);
        building.scale.setScalar(CITY * 0.85);
        building.rotation.y = side > 0 ? -Math.PI / 2 : Math.PI / 2;
        building.position.set(baseX + 3 * side + side * this.frontOffset(key, CITY * 0.85), 0, 0);
        segment.add(building);
      } else {
        const lawn = new THREE.Mesh(this.box(18, 0.3, SEGMENT), this.flat(0x7fc77f));
        lawn.position.set(baseX + side * 9, 0.15, 0);
        segment.add(lawn);
        for (const z of [-3.5, 3.5]) {
          const treeKey = this.random() < 0.5 ? "city-kit-suburban/tree-large" : "city-kit-suburban/tree-small";
          const tree = this.assets.clone(treeKey);
          tree.scale.setScalar(CITY * 0.95);
          const crown = Math.max(this.assets.info(treeKey).size.x, this.assets.info(treeKey).size.z) * CITY * 0.95 / 2;
          tree.position.set(side * Math.max(Math.abs(baseX) + 3 + this.random() * 9, Math.abs(baseX) + 0.5 + crown), 0.3, z);
          segment.add(tree);
        }
      }
      if (n % 5 === 1) {
        const planter = this.assets.fit("city-kit-suburban/planter", { x: 2.2 });
        planter.position.set(side * (ROAD_HALF + 3.6), 0.34, 2);
        segment.add(planter);
      }
    }
    // (the decorative overpass is gone: it had no collision — a hop from a truck roof went through its deck, and it could
    // cut through the red bridge — and its gantry sign hung 3.4 m in the air; the red bridge is the solid bridge now)
  }

  /** A street lamp on its slice: placed by the POLE (the fitted box includes the arm: the pole sat 0.97 m off, half over the
   *  road, 0.34 m up); `lampX` = the pole's |x| plus that offset. */
  private streetLamp(side: number): THREE.Group {
    const lamp = this.assets.fit("city-kit-roads/light-curved", { y: 7.5 }, side > 0 ? 3 : 1);
    lamp.position.set(side * this.lampX, 0.34, 0);
    return lamp;
  }

  /** How far a building turned side-on (±90°) sits from the building line to its origin so that its REAL near face — the
   *  model's whole box, porches and awnings included — lands on the line (2026-10-17: off-centre models stuck out past
   *  15.1 m and the billboards on the sidewalk went half inside them). */
  private frontOffset(key: string, scale: number): number {
    const info = this.assets.info(key);
    return (info.min.z + info.size.z) * scale;                  // turned side-on, the model's +z end faces the road
  }

  /** Where a billboard may stand: just in front of the next street lamp, so the pole hides behind the board (2.2 m behind its
   *  face) instead of standing on it; the next lamp in front is 24 m off and only crosses the board far away, briefly. */
  private signSpot = (z: number): number => {
    const BEHIND = 2.2;
    let lamp = Infinity;
    for (const segment of this.segments) {
      if (!segment.userData.lamp) continue;
      const lampZ = (segment.userData.cz as number) - this.cityScroll;
      if (lampZ >= z + BEHIND && lampZ < lamp) lamp = lampZ;
    }
    return Number.isFinite(lamp) ? lamp - BEHIND : z;
  };

  private scroll(distance: number): void {
    if (!this.holdSigns) this.eggs?.scroll(distance);            // the billboards move with the city, whatever the state
    // one texture repeat per slice; v runs towards the camera, so the paint moves with the city when the offset falls
    this.streetScroll = (this.streetScroll - distance / SEGMENT) % 1;
    for (const map of this.streetMaps) map.offset.y = this.streetScroll;
    this.cityScroll += distance;
    if (this.cityScroll > LOOP * 4 && this.city) {               // keep city-space numbers small on long runs
      for (const segment of this.segments) {
        segment.userData.cz -= LOOP * 4;
        this.city.shift(segment.userData.handles as CityHandle[], -LOOP * 4);
      }
      this.cityScroll -= LOOP * 4;
    }
    if (this.city) this.city.group.position.z = -this.cityScroll;
    for (const segment of this.segments) {
      if ((segment.userData.cz as number) - this.cityScroll < -36) {
        segment.userData.cz += LOOP;
        this.dress(segment);
      }
    }
  }

  /** Re-dresses one slice: its old instances go, decorate() builds the new ones, the batches take them. */
  private dress(segment: THREE.Group): void {
    this.city?.release(segment.userData.handles as CityHandle[] | undefined);
    this.decorate(segment);
    segment.userData.handles = this.city?.take(segment, segment.userData.cz as number) ?? [];
  }

  /** Every model and box decorate() can place, grouped by material; the roads kit (lamps, pillars, signs) casts. */
  private cityBatches(): CityBatches {
    const keys = [...MID_BUILDINGS, ...TALL_BUILDINGS, ...INDUSTRY, "city-kit-suburban/tree-large", "city-kit-suburban/tree-small",
      "city-kit-suburban/planter", "city-kit-roads/light-curved", "city-kit-roads/bridge-pillar-wide", "city-kit-roads/sign-highway-wide"];
    const parts: { geometry: THREE.BufferGeometry; material: THREE.Material }[] = [];
    const casters = new Set<THREE.Material>();
    for (const key of keys) {
      if (!this.assets.has(key)) continue;
      this.assets.info(key).scene.traverse((o) => {
        const mesh = o as THREE.Mesh;
        if (!mesh.isMesh || Array.isArray(mesh.material)) return;
        parts.push({ geometry: mesh.geometry, material: mesh.material });
      });
    }
    const boxes = [this.box(18, 0.3, SEGMENT), this.box(60, 1.4, 9), this.box(60, 0.3, 8), this.box(60, 1.0, 0.3)];
    return new CityBatches(parts, boxes, casters, (geometry, material) => withNightGlow(geometry, material.name));
  }

  // ------------------------------------------------------------------------------------------------ entities

  private syncEntities(entities: WorldEntity[], dt: number): void {
    const activeIds = this.activeIds;
    activeIds.clear();
    const props = this.props;
    props.begin(this.clock);
    const follow = Math.min(1, dt * 10);
    for (let i = 0; i < entities.length; i++) {
      const entity = entities[i];
      if (!entity.active) { if (entity.cleared) this.blowAway(entity); continue; }
      const rel = entity.z - this.carZ;
      if (rel + entity.depth < -34 || rel > 420) continue;          // hide only once its far end is behind the camera
      activeIds.add(entity.id);
      if (entity.kind === "coin" || entity.kind === "ethCoin") {   // the instanced fields: y = the coin's centre / the road
        const field = entity.kind === "coin" ? this.solCoins : this.ethCoins;
        field.add(entity.id, VX * entity.x, entity.kind === "coin" ? entity.yBase + 0.75 : entity.yBase, rel + entity.depth / 2);
        this.coinIds.add(entity.id);
        continue;
      }
      if (PROP_KINDS.has(entity.kind)) {                             // instanced props: placed again every frame
        let live = this.live.get(entity.id);
        if (!live || live.entity !== entity) {
          live = { entity, proto: this.propFor(entity), x: VX * entity.x };
          this.live.set(entity.id, live);
        }
        live.x += (VX * entity.x - live.x) * follow;
        // drawn where its collision box is, always; pickups at the icon's centre (the prop floats and turns inside)
        const y = PICKUPS.includes(entity.kind) ? entity.yBase + 0.8 : 0;
        props.place(live.proto, entity.id, live.x, y, rel + entity.depth / 2, entity);
        continue;
      }
      let object = this.entityObjects.get(entity.id);
      if (!object) {
        object = this.acquire(entity);
        object.position.x = VX * entity.x;
        object.position.y = 0;
        this.entityObjects.set(entity.id, object);
        this.scene.add(object);
      }
      object.visible = true;
      object.position.x += (VX * entity.x - object.position.x) * follow;
      object.position.z = rel + entity.depth / 2;
      animateProp(object, this.clock, entity.id);                  // coin spin, pickup float, lamps, glows (props only)
      object.position.y = 0;                                       // drawn where its collision box is, always
    }
    props.end();
    this.live.forEach(this.dropProp);
    this.coinIds.forEach(this.dropCoin);
    this.solCoins.update(this.clock);
    this.ethCoins.update(this.clock);
    this.entityObjects.forEach(this.dropObject);
  }

  /**
   * Something taken off the road on purpose — the road ahead cleared by a board or shield save or a continue, the truck a
   * board broke on, an ETH coin knocked away — leaves in a puff of smoke and sparks where it stood, carried back with the
   * road, instead of popping out of sight (Karolis 2026-10-17: "sometimes things disappear under me or in front").
   */
  private blowAway(entity: WorldEntity): void {
    if (this.blown.has(entity)) return;
    this.blown.add(entity);
    const rel = entity.z - this.carZ;
    if (rel + entity.depth < -12 || rel > 180) return;                  // behind the camera, or far in the haze
    const x = VX * entity.x, drift = -this.roadSpeed;
    const big = Math.min(3.2, 0.9 + entity.height * 0.45);              // a truck's puffs are a few metres across, a barrier's one
    const spots = Math.max(1, Math.min(7, Math.round(entity.depth / 6)));
    for (let i = 0; i < spots; i++) {
      const z = rel + (entity.depth * (i + 0.5)) / spots;
      this.burst(x, entity.yBase + entity.height * 0.45, z, 0xd9d3e8, 2, 2 + entity.height * 0.6, true, drift, big);
    }
    this.burst(x, entity.yBase + entity.height * 0.6, rel + Math.min(entity.depth, 4) / 2, 0xfff1c9, 8, 7, false, drift);
  }

  /** (bound once: no closures per frame) a prop, coin or pooled object whose entity left the road this frame */
  private dropProp = (_live: LiveProp, id: number): void => {
    if (!this.activeIds.has(id)) this.live.delete(id);
  };

  private dropCoin = (id: number): void => {
    if (this.activeIds.has(id)) return;
    this.solCoins.remove(id);
    this.ethCoins.remove(id);
    this.coinIds.delete(id);
  };

  private dropObject = (object: THREE.Object3D, id: number): void => {
    if (this.activeIds.has(id)) return;
    this.entityObjects.delete(id);
    object.visible = false;
    this.scene.remove(object);
    const key = object.userData.key as string;
    const pool = this.pools.get(key) ?? [];
    pool.push(object);
    this.pools.set(key, pool);
  };

  /**
   * The instanced model for an entity: one prepared prototype per look (kind; trailers and paint for trucks; the power;
   * the letter) — what `buildEntityProp` builds from. The danger box is drawn per entity from its own collision box.
   */
  private propFor(entity: WorldEntity): PropProto {
    const truck = entity.kind === "truck" || entity.kind === "mover";
    const variant = truck ? entity.id % 3 : 0;
    const key = `${entity.kind}:${truck ? entity.trailers : 0}:${entity.kind === "pickup" ? entity.power ?? "" : ""}:${entity.kind === "letter" ? entity.letter ?? "" : ""}:${variant}`;
    const known = this.props.get(key);
    if (known) return known;
    const root = buildEntityProp(entity, variant) as THREE.Object3D;
    this.prepareEntity(root, entity.kind);
    return this.props.proto(key, entity.kind, root, DANGER_KINDS.includes(entity.kind));
  }

  private acquire(entity: WorldEntity): THREE.Object3D {
    const variant = entity.kind === "truck" || entity.kind === "mover" ? entity.id % 3 : 0;
    const key = `${entity.kind}:${entity.trailers}:${entity.power ?? ""}:${entity.letter ?? ""}:${Math.round(entity.depth)}:${variant}`;
    const reused = this.pools.get(key)?.pop();
    if (reused) return reused;
    const object = this.createEntityObject(entity, variant);
    if (DANGER_KINDS.includes(entity.kind)) {
      const box = new THREE.Mesh(this.geo("unit-box", () => new THREE.BoxGeometry(1, 1, 1)), this.dangerMaterial);
      box.scale.set(entity.width + 0.08, entity.height + 0.08, Math.max(0.3, entity.depth) + 0.08);
      box.position.y = entity.yBase + entity.height / 2;
      box.name = "danger";
      object.add(box);
    }
    this.prepareEntity(object, entity.kind);
    object.userData.key = key;
    return object;
  }

  private shadowPlane(width: number, depth: number): THREE.Mesh {
    const shadow = new THREE.Mesh(new THREE.PlaneGeometry(width, depth), this.mat("blob", () => new THREE.MeshBasicMaterial({ map: this.blob, transparent: true, depthWrite: false })));
    shadow.rotation.x = -Math.PI / 2;
    shadow.position.y = 0.025;
    return shadow;
  }

  private geo(key: string, make: () => THREE.BufferGeometry): THREE.BufferGeometry {
    let g = this.geometries.get(key);
    if (!g) { g = make(); this.geometries.set(key, g); }
    return g;
  }

  private glowTexture(): THREE.Texture {
    return this.tex("glow", () => canvasTexture(128, 128, (ctx) => {
      const g = ctx.createRadialGradient(64, 64, 4, 64, 64, 64);
      g.addColorStop(0, "rgba(255,255,255,0.9)");
      g.addColorStop(0.35, "rgba(255,255,255,0.35)");
      g.addColorStop(1, "rgba(255,255,255,0)");
      ctx.fillStyle = g;
      ctx.fillRect(0, 0, 128, 128);
    }));
  }

  /** The ramp deck: yellow with black chevrons pointing up the slope. */
  private chevrons(): THREE.Texture {
    return this.tex("chevrons-deck", () => {
      const t = canvasTexture(256, 512, (ctx) => {
        ctx.fillStyle = "#ffd400";
        ctx.fillRect(0, 0, 256, 512);
        ctx.fillStyle = "#151515";
        for (let y = -64; y < 560; y += 96) {
          ctx.beginPath();
          ctx.moveTo(0, y + 60); ctx.lineTo(128, y); ctx.lineTo(256, y + 60); ctx.lineTo(256, y + 100); ctx.lineTo(128, y + 40); ctx.lineTo(0, y + 100);
          ctx.closePath(); ctx.fill();
        }
        ctx.fillStyle = "#14f195";
        ctx.beginPath(); ctx.moveTo(128, 200); ctx.lineTo(176, 268); ctx.lineTo(80, 268); ctx.closePath(); ctx.fill();
      });
      return t;
    });
  }

  private tex(key: string, draw: () => THREE.Texture): THREE.Texture {
    let t = this.textures.get(key);
    if (!t) { t = draw(); this.textures.set(key, t); }
    return t;
  }

  private label(text: string, bg: string, fg = "#ffffff", w = 256, h = 256): THREE.Texture {
    return this.tex(`label:${text}:${bg}:${fg}:${w}x${h}`, () => canvasTexture(w, h, (ctx) => {
      ctx.fillStyle = bg;
      ctx.fillRect(0, 0, w, h);
      ctx.fillStyle = fg;
      ctx.textAlign = "center";
      ctx.textBaseline = "middle";
      const lines = text.split("\n");
      const size = Math.floor(h / (lines.length + 0.5));
      ctx.font = `900 ${size}px Impact, "Arial Black", sans-serif`;
      lines.forEach((line, i) => ctx.fillText(line, w / 2, h / 2 + (i - (lines.length - 1) / 2) * size * 1.02));
    }));
  }

  private icon(text: string, bg: string): THREE.Texture {
    return this.tex(`icon:${text}:${bg}`, () => canvasTexture(256, 256, (ctx) => {
      ctx.fillStyle = bg;
      ctx.beginPath();
      ctx.arc(128, 128, 120, 0, Math.PI * 2);
      ctx.fill();
      ctx.lineWidth = 14;
      ctx.strokeStyle = "#ffffff";
      ctx.stroke();
      ctx.fillStyle = "#ffffff";
      ctx.textAlign = "center";
      ctx.textBaseline = "middle";
      ctx.font = `900 ${text.length > 1 ? 104 : 130}px Impact, "Arial Black", sans-serif`;
      ctx.fillText(text, 128, 136);
    }));
  }

  private refreshCoinFace(mark: THREE.Texture): void {
    const image = mark.image as HTMLImageElement;
    this.solFace.map = canvasTexture(256, 256, (ctx) => {
      const g = ctx.createRadialGradient(110, 100, 10, 128, 128, 128);
      g.addColorStop(0, "#ffffff"); g.addColorStop(1, "#efe6ff");
      ctx.fillStyle = g;
      ctx.beginPath(); ctx.arc(128, 128, 126, 0, Math.PI * 2); ctx.fill();
      ctx.lineWidth = 16; ctx.strokeStyle = "#14f195"; ctx.stroke();
      const w = 160, h = w * (image.height / Math.max(1, image.width));
      ctx.drawImage(image, 128 - w / 2, 128 - h / 2, w, h);
    });
    this.solFace.needsUpdate = true;
  }

  private refreshEthFace(glyph: THREE.Texture): void {
    const image = glyph.image as HTMLImageElement;
    this.ethFace.map = canvasTexture(256, 256, (ctx) => {
      ctx.fillStyle = "#0d0d10";
      ctx.beginPath(); ctx.arc(128, 128, 126, 0, Math.PI * 2); ctx.fill();
      ctx.lineWidth = 14; ctx.strokeStyle = "#ff1f3d"; ctx.stroke();
      // the glyph in red: draw it, then tint
      const h = 168, w = h * (image.width / Math.max(1, image.height));
      const glyph = document.createElement("canvas");
      glyph.width = Math.ceil(w); glyph.height = h;
      const g = glyph.getContext("2d");
      if (g) {
        g.drawImage(image, 0, 0, w, h);
        g.globalCompositeOperation = "source-in";
        g.fillStyle = "#ff2a45";
        g.fillRect(0, 0, w, h);
        ctx.drawImage(glyph, 128 - w / 2, 128 - h / 2);
      }
    });
    this.ethFace.color.set(0xffffff);
    this.ethFace.needsUpdate = true;
  }

  /** Road trains: ETH containers on flatbeds; a cab at the far end of parked ones, facing you on oncoming ones. */
  private truckMesh(entity: WorldEntity, variant: number, oncoming: boolean): THREE.Group {
    const group = new THREE.Group();
    const n = Math.max(1, entity.trailers);
    const total = trailerDepth(n);
    const cabLength = n === 1 ? 0 : 6.6;
    const wagons = total - cabLength;
    const each = wagons / n;
    const chassis = this.flat(0x3b3d52);
    for (let i = 0; i < n; i++) {
      const zc = -total / 2 + each * (i + 0.5);
      const bed = new THREE.Mesh(new THREE.BoxGeometry(3.1, 0.5, each - 0.5), chassis);
      bed.position.set(0, 1.0, zc);
      group.add(bed);
      for (const dz of [-(each / 2 - 1.4), each / 2 - 1.4]) {
        for (const x of [-1.3, 1.3]) {
          const wheel = this.assets.fit("car-kit/wheel-truck", { y: 1.1 });
          wheel.position.set(x, 0, zc + dz);
          group.add(wheel);
        }
      }
      const container = this.assets.fit(CONTAINERS[(variant + i) % 3], { x: 3.4, y: ROOF_HEIGHT - 1.25, z: each - 0.35 });
      container.position.set(0, 1.25, zc);
      group.add(container);
      for (const side of [-1, 1]) {
        const logo = new THREE.Mesh(new THREE.PlaneGeometry(2.0, 3.0), this.ethDecal);
        logo.position.set(side * 1.72, 1.25 + (ROOF_HEIGHT - 1.25) / 2, zc);
        logo.rotation.y = side * Math.PI / 2;
        group.add(logo);
      }
    }
    const back = new THREE.Mesh(new THREE.PlaneGeometry(2.2, 3.2), this.ethDecal);
    back.position.set(0, 1.25 + (ROOF_HEIGHT - 1.25) / 2, -total / 2 - 0.03);
    back.rotation.y = Math.PI;
    group.add(back);
    for (const x of [-1.25, 1.25]) {
      const tail = new THREE.Mesh(new THREE.BoxGeometry(0.45, 0.3, 0.08), this.glow(0xff3348, 3));
      tail.position.set(x, 1.0, -total / 2 - 0.05);
      group.add(tail);
    }
    if (cabLength > 0) {
      const cab = this.assets.fit(n % 2 ? "car-kit/delivery" : "car-kit/garbage-truck", { x: 3.4, y: ROOF_HEIGHT, z: cabLength });
      cab.position.set(0, 0, total / 2 - cabLength / 2);
      group.add(cab);
    }
    if (oncoming) {
      for (const x of [-1.15, 1.15]) {
        const head = new THREE.Mesh(new THREE.CircleGeometry(0.32, 16), this.glow(0xfffbe0, 4));
        head.position.set(x, 1.3, total / 2 + 0.05);
        group.add(head);
      }
      const beam = new THREE.Mesh(new THREE.PlaneGeometry(3.4, 10), this.mat("beam", () => new THREE.MeshBasicMaterial({ color: 0xfff6c8, transparent: true, opacity: 0.28, depthWrite: false })));
      beam.rotation.x = -Math.PI / 2;
      beam.position.set(0, 0.04, total / 2 + 5);
      group.add(beam);
      group.rotation.y = Math.PI;
    }
    group.add(this.shadowPlane(4.8, total + 1.5));
    return group;
  }

  private stripes(width: number, height: number, a = "#e4393c", b = "#ffffff"): THREE.Texture {
    return this.tex(`stripes:${width}:${height}:${a}:${b}`, () => canvasTexture(256, 64, (ctx) => {
      const n = 8;
      for (let i = 0; i < n; i++) {
        ctx.fillStyle = i % 2 ? b : a;
        ctx.beginPath();
        const x = (i / n) * 256;
        ctx.moveTo(x, 0); ctx.lineTo(x + 32, 0); ctx.lineTo(x + 32 - 24, 64); ctx.lineTo(x - 24, 64); ctx.closePath();
        ctx.fill();
      }
    }));
  }

  private createEntityObject(entity: WorldEntity, variant: number): THREE.Object3D {
    // the studio props (src/world/props) for every kind they cover; the builders below only for the rest (gate,
    // blocker, platform, speed pads)
    const prop = buildEntityProp(entity, variant);
    if (prop) return prop;
    const kind = entity.kind;
    const group = new THREE.Group();
    const warnA = this.settings.highContrast ? "#ffd400" : "#e4393c";
    if (kind === "coin") {
      // SOL: a thick spinning coin with the mark on both faces and a soft glow, so it reads from far away
      const spin = new THREE.Group();
      const coin = new THREE.Mesh(this.geo("coin", () => new THREE.CylinderGeometry(0.58, 0.58, 0.2, 28)), [this.mat("coin-rim", () => new THREE.MeshBasicMaterial({ color: 0x8a3dff })), this.solFace, this.solFace]);
      coin.rotation.x = Math.PI / 2;
      spin.add(coin);
      spin.name = "spin";
      const glow = new THREE.Sprite(this.mat("coin-glow", () => new THREE.SpriteMaterial({ map: this.glowTexture(), color: new THREE.Color(0xb98cff).multiplyScalar(1.5), transparent: true, depthWrite: false, blending: THREE.AdditiveBlending })) as THREE.SpriteMaterial);
      glow.scale.set(2.0, 2.0, 1);
      group.add(glow, spin);
      return group;
    }
    if (kind === "ethCoin") {
      // ETH: danger. A big black coin with a red glyph, red ring and hazard base — never mistaken for SOL
      const face = new THREE.Group();
      const coin = new THREE.Mesh(this.geo("eth", () => new THREE.CylinderGeometry(0.95, 0.95, 0.26, 30)), [this.mat("eth-rim", () => new THREE.MeshBasicMaterial({ color: 0xff1f3d })), this.ethFace, this.ethFace]);
      coin.rotation.x = Math.PI / 2;
      face.add(coin);
      face.position.y = 1.05;
      face.name = "spin";
      const ring = new THREE.Mesh(this.geo("eth-ring", () => new THREE.TorusGeometry(1.08, 0.07, 8, 34)), this.mat("eth-ring", () => new THREE.MeshBasicMaterial({ color: 0xff1f3d })));
      ring.position.y = 1.05;
      const glow = new THREE.Sprite(this.mat("eth-glow", () => new THREE.SpriteMaterial({ map: this.glowTexture(), color: new THREE.Color(0xff2040).multiplyScalar(1.6), transparent: true, depthWrite: false, blending: THREE.AdditiveBlending })) as THREE.SpriteMaterial);
      glow.scale.set(3.2, 3.2, 1);
      glow.position.y = 1.05;
      const base = new THREE.Mesh(this.geo("eth-base", () => new THREE.CylinderGeometry(0.55, 0.7, 0.18, 18)), new THREE.MeshBasicMaterial({ map: this.stripes(2, 1, "#ff1f3d", "#111111") }));
      base.position.y = 0.09;
      const post = new THREE.Mesh(this.box(0.12, 0.2, 0.12), this.flat(0x111111));
      post.position.y = 0.2;
      group.add(glow, base, post, face, ring, this.shadowPlane(2.2, 2.2));
      return group;
    }
    if (PICKUPS.includes(kind)) {
      if (kind === "mysteryBox") {
        const box = this.assets.fit("car-kit/box", { x: 1.2 });
        const sticker = new THREE.Mesh(new THREE.PlaneGeometry(0.7, 0.7), new THREE.MeshBasicMaterial({ map: this.icon("?", "#9945ff") }));
        sticker.position.set(0, 0.6, -0.61);
        sticker.rotation.y = Math.PI;
        group.add(box, sticker, this.shadowPlane(2, 2));
        return group;
      }
      if (kind === "key") {
        const gold = this.metal(0xffc928);
        const ring = new THREE.Mesh(new THREE.TorusGeometry(0.38, 0.12, 8, 20), gold);
        const shaft = new THREE.Mesh(new THREE.BoxGeometry(0.18, 0.9, 0.14), gold);
        shaft.position.y = -0.72;
        const tooth = new THREE.Mesh(new THREE.BoxGeometry(0.36, 0.16, 0.14), gold);
        tooth.position.set(0.14, -1.05, 0);
        const holder = new THREE.Group();
        holder.add(ring, shaft, tooth);
        holder.position.y = 0.8;
        group.add(holder, this.shadowPlane(1.8, 1.8));
        return group;
      }
      const colors: Record<string, string> = { magnet: "#ff4d6d", x2: "#9945ff", jetpack: "#1fa2ff", sneakers: "#ffb400", pogo: "#ff7a1a", mystery: "#5b6275", headstart: "#16b3c8", booster: "#16a34a", board: "#ff5fd2", shield: "#3b7ddd" };
      const labels: Record<string, string> = { magnet: "M", x2: "2X", jetpack: "J", sneakers: "S", pogo: "P", mystery: "?", headstart: "H", booster: "+", board: "B", shield: "O" };
      const bg = kind === "letter" ? "#ffffff" : kind === "token" ? "#14b87a" : colors[entity.power ?? "magnet"] ?? "#5b6275";
      const text = kind === "letter" ? (entity.letter ?? "A") : kind === "token" ? "◎" : labels[entity.power ?? "magnet"] ?? "?";
      const face = new THREE.Mesh(new THREE.CircleGeometry(0.62, 28), new THREE.MeshBasicMaterial({ map: kind === "letter" ? this.label(text, "#ffffff", "#1b1d2b", 128, 128) : this.icon(text, bg), side: THREE.DoubleSide }));
      const ring = new THREE.Mesh(new THREE.TorusGeometry(0.66, 0.08, 10, 30), this.glow(0xffffff, 1.4));
      group.add(face, ring, this.pickupGlow(bg), this.shadowPlane(2, 2));
      return group;
    }
    if (kind === "truck" || kind === "mover") {
      group.add(this.truckMesh(entity, variant, kind === "mover"));
      return group;
    }
    if (kind === "ramp") {
      // a ramp trailer: a bright yellow deck with black chevrons and green arrows, white rails, on a flatbed
      const deck = new THREE.Mesh(rampGeometry(3.3, ROOF_HEIGHT - 0.04, entity.depth, RAMP_SLOPE), this.flat(0x4b4f6b));
      group.add(deck);
      const slopeLength = Math.hypot(RAMP_SLOPE, ROOF_HEIGHT);
      const angle = Math.atan2(ROOF_HEIGHT, RAMP_SLOPE);
      const surface = new THREE.Mesh(new THREE.PlaneGeometry(3.0, slopeLength), new THREE.MeshBasicMaterial({ map: this.chevrons() }));
      surface.rotation.set(-Math.PI / 2 - angle, 0, Math.PI);      // up the slope, facing you (it faced away: never drawn)
      surface.position.set(0, ROOF_HEIGHT / 2 + 0.04, -entity.depth / 2 + RAMP_SLOPE / 2);
      group.add(surface);
      const top = new THREE.Mesh(new THREE.PlaneGeometry(3.0, entity.depth - RAMP_SLOPE), this.flat(0xffd400));
      top.rotation.x = -Math.PI / 2;
      top.position.set(0, ROOF_HEIGHT + 0.01, -entity.depth / 2 + RAMP_SLOPE + (entity.depth - RAMP_SLOPE) / 2);
      group.add(top);
      for (const x of [-1.65, 1.65]) {
        const rail = new THREE.Mesh(this.box(0.16, 0.16, slopeLength), this.flat(0xffffff));
        rail.rotation.x = -angle;
        rail.position.set(x, ROOF_HEIGHT / 2 + 0.25, -entity.depth / 2 + RAMP_SLOPE / 2);
        group.add(rail);
        const side = new THREE.Mesh(new THREE.PlaneGeometry(entity.depth - RAMP_SLOPE + 0.5, ROOF_HEIGHT - 1.2), this.mat("ramp-side", () => new THREE.MeshStandardMaterial({ color: 0x3b7ddd, side: THREE.DoubleSide, roughness: 0.6, metalness: 0 })));
        side.rotation.y = Math.PI / 2;
        side.position.set(x * 1.01, 1.2 + (ROOF_HEIGHT - 1.2) / 2, -entity.depth / 2 + RAMP_SLOPE + (entity.depth - RAMP_SLOPE) / 2);
        group.add(side);
        const logo = new THREE.Mesh(new THREE.PlaneGeometry(1.6, 2.4), this.ethDecal);
        logo.rotation.y = x > 0 ? Math.PI / 2 : -Math.PI / 2;
        logo.position.set(x * 1.03, 2.8, -entity.depth / 2 + RAMP_SLOPE + (entity.depth - RAMP_SLOPE) / 2);
        group.add(logo);
      }
      for (const dz of [-entity.depth / 2 + 1.4, entity.depth / 2 - 1.6]) {
        for (const x of [-1.3, 1.3]) {
          const wheel = this.assets.fit("car-kit/wheel-truck", { y: 1.0 });
          wheel.position.set(x, 0, dz);
          group.add(wheel);
        }
      }
      for (const x of [-1.2, 1.2]) {
        const lamp = new THREE.Mesh(this.box(0.4, 0.2, 0.08), this.glow(0xffb000, 3));
        lamp.position.set(x, 0.35, -entity.depth / 2 - 0.05);
        group.add(lamp);
      }
      group.add(this.shadowPlane(4.6, entity.depth + 1));
      return group;
    }
    if (kind === "barrierLow") {
      const barrier = this.assets.fit("city-kit-roads/construction-barrier", { x: 3.7, y: entity.height, z: 1.1 }, 1);
      const band = new THREE.Mesh(new THREE.PlaneGeometry(3.3, 0.55), new THREE.MeshBasicMaterial({ map: this.stripes(4, 1, warnA) }));
      band.position.set(0, entity.height * 0.62, -0.58);
      band.rotation.y = Math.PI;
      group.add(barrier, band, this.shadowPlane(4.4, 2.2));
      return group;
    }
    if (kind === "barrierBar") {
      const top = entity.yBase + entity.height;
      const fence = this.assets.fit("city-kit-roads/construction-fence", { x: 3.9, y: top, z: 0.5 }, 1);
      const band = new THREE.Mesh(new THREE.PlaneGeometry(3.4, entity.height * 0.7), new THREE.MeshBasicMaterial({ map: this.stripes(4, 1, warnA) }));
      band.position.set(0, entity.yBase + entity.height * 0.5, -0.28);
      band.rotation.y = Math.PI;
      group.add(fence, band, this.shadowPlane(4.4, 1.6));
      return group;
    }
    if (kind === "barrierHigh") {
      for (const x of [-1.85, 1.85]) {
        const post = new THREE.Mesh(new THREE.BoxGeometry(0.3, ROOF_HEIGHT + 0.3, 0.3), this.flat(0x6f7189));
        post.position.set(x, (ROOF_HEIGHT + 0.3) / 2, 0);
        group.add(post);
      }
      const panel = new THREE.Mesh(new THREE.BoxGeometry(3.8, entity.height, 0.3), this.flat(0x2f8f5b));
      panel.position.y = entity.yBase + entity.height / 2;
      const face = new THREE.Mesh(new THREE.PlaneGeometry(3.5, entity.height - 0.3), new THREE.MeshBasicMaterial({ map: this.label("LOW\nCLEARANCE\n▼ DUCK ▼", "#2f8f5b", "#ffffff", 256, 280) }));
      face.position.set(0, entity.yBase + entity.height / 2 + 0.1, -0.16);
      face.rotation.y = Math.PI;
      const hazard = new THREE.Mesh(new THREE.PlaneGeometry(3.8, 0.35), new THREE.MeshBasicMaterial({ map: this.stripes(4, 1, "#ffd400", "#2b2f3a") }));
      hazard.position.set(0, entity.yBase + 0.18, -0.17);
      hazard.rotation.y = Math.PI;
      group.add(panel, face, hazard, this.shadowPlane(4.4, 1.6));
      return group;
    }
    if (kind === "pole") {
      const light = this.assets.fit("city-kit-roads/traffic-light", { y: entity.height }, 2);
      group.add(light, this.shadowPlane(1.8, 1.8));
      return group;
    }
    if (kind === "pillar") {
      const pillar = this.assets.fit("city-kit-roads/bridge-pillar-wide", { x: 1.3, y: entity.height, z: 1.3 });
      group.add(pillar, this.shadowPlane(2.2, 2.2));
      return group;
    }
    if (kind === "bush") {
      const planter = this.assets.fit("city-kit-suburban/planter", { x: 2.6, z: 2.4 }, 1);
      group.add(planter, this.shadowPlane(3, 3));
      return group;
    }
    if (kind === "dumpster") {
      const dumpster = this.assets.fit("city-kit-roads/dumpster", { x: entity.width, y: entity.height, z: Math.max(1.6, entity.depth) });
      group.add(dumpster, this.shadowPlane(entity.width + 1, 3));
      return group;
    }
    if (kind === "powerBox") {
      const box = this.assets.fit("car-kit/box", { x: entity.width, y: entity.height, z: Math.max(1.2, entity.depth) });
      group.add(box, this.shadowPlane(entity.width + 1, 2.4));
      return group;
    }
    if (kind === "blocker") {
      const block = new THREE.Mesh(new THREE.BoxGeometry(entity.width, entity.height, 0.6), this.flat(0xe4393c));
      block.position.y = entity.height / 2;
      const sign = new THREE.Mesh(new THREE.PlaneGeometry(entity.width - 0.4, 1.4), new THREE.MeshBasicMaterial({ map: this.label("ROAD\nCLOSED", "#e4393c") }));
      sign.position.set(0, entity.height * 0.6, -0.31);
      sign.rotation.y = Math.PI;
      group.add(block, sign);
      return group;
    }
    if (kind === "platform") {
      const slab = new THREE.Mesh(new THREE.BoxGeometry(entity.width, entity.height, entity.depth), this.flat(0xd9d4ea));
      slab.position.y = entity.height / 2;
      group.add(slab);
      return group;
    }
    if (kind === "gate") {
      return group;                                              // the bridge is drawn by its deck
    }
    if (kind === "gateWall") {
      // the bridge's wall in a blocked lane: dark red steel, a hazard band and a no-entry disc facing you
      const wall = new THREE.Mesh(this.box(entity.width, entity.height, entity.depth), this.flat(0x9e2a20));
      wall.position.y = entity.height / 2;
      const band = new THREE.Mesh(new THREE.PlaneGeometry(entity.width, 0.9), new THREE.MeshBasicMaterial({ map: this.stripes(4, 1, "#ffd400", "#2b2f3a") }));
      band.position.set(0, 0.45, -entity.depth / 2 - 0.02);
      band.rotation.y = Math.PI;
      const sign = new THREE.Mesh(new THREE.CircleGeometry(1.1, 32), new THREE.MeshBasicMaterial({ map: this.label("⛔", "#ffffff", "#e4393c", 256, 256) }));
      sign.position.set(0, 3.2, -entity.depth / 2 - 0.03);
      sign.rotation.y = Math.PI;
      group.add(wall, band, sign);
      return group;
    }
    if (kind === "deck") {
      // a red steel bridge over all lanes: deck, truss sides, pillars outside the road — red means you can drive on it
      const redM = this.flat(0xd8352a), darkRed = this.flat(0x8f1f18);
      const slab = new THREE.Mesh(this.box(entity.width, entity.height, entity.depth), redM);
      slab.position.y = entity.yBase + entity.height / 2;
      const walk = new THREE.Mesh(this.box(entity.width - 0.6, 0.06, entity.depth), this.flat(0x6d7190));
      walk.position.y = entity.yBase + entity.height + 0.03;
      group.add(slab, walk);
      for (const side of [-1, 1]) {
        const rail = new THREE.Mesh(this.box(0.25, 1.1, entity.depth), darkRed);
        rail.position.set(side * (entity.width / 2 - 0.12), entity.yBase + entity.height + 0.55, 0);
        group.add(rail);
        for (let i = -3; i <= 3; i++) {
          const strut = new THREE.Mesh(this.box(0.18, 1.6, 0.18), darkRed);
          strut.position.set(side * (entity.width / 2 - 0.1), entity.yBase + entity.height * 0.5, i * entity.depth / 7);
          strut.rotation.x = i % 2 ? 0.6 : -0.6;
          group.add(strut);
        }
        for (const dz of [-entity.depth / 2 + 2, entity.depth / 2 - 2]) {
          const pillar = new THREE.Mesh(this.box(1.2, entity.yBase, 1.2), darkRed);
          pillar.position.set(side * (ROAD_HALF + 1.3), entity.yBase / 2, dz);
          group.add(pillar);
          const arm = new THREE.Mesh(this.box(ROAD_HALF + 1.3 - entity.width / 2 + 0.6, 0.6, 1.0), darkRed);
          arm.position.set(side * ((ROAD_HALF + 1.3 + entity.width / 2) / 2), entity.yBase + 0.3, dz);
          group.add(arm);
        }
      }
      const sign = new THREE.Mesh(new THREE.PlaneGeometry(8, 1.4), new THREE.MeshBasicMaterial({ map: this.label("SOL RUN  ➜", "#1d7044", "#ffffff", 512, 96) }));
      sign.position.set(0, entity.yBase - 0.8, -entity.depth / 2 - 0.02);
      sign.rotation.y = Math.PI;
      group.add(sign, this.shadowPlane(entity.width + 2, entity.depth));
      return group;
    }
    if (kind === "speedPadBoost" || kind === "speedPadSlow") {
      const pad = new THREE.Mesh(new THREE.BoxGeometry(3.4, 0.08, entity.depth), this.glow(kind === "speedPadBoost" ? GREEN : 0xff4d6d, 1.6));
      pad.position.y = 0.04;
      group.add(pad);
      return group;
    }
    const cone = this.assets.fit("car-kit/cone", { y: 1.0 });
    group.add(cone);
    return group;
  }
}
